import { createHash, randomUUID, timingSafeEqual } from 'crypto';
import { type Server } from 'http';
import { serve } from '@hono/node-server';
import { Authorization } from '@okrapdf/schemas/http-authorization';
import {
  ComponentAssertionRequirementSchema,
  layerComponentGraders,
  runComponentAssertionRequirementsEffect,
  standardComponentEvaluatorHandlers,
  type ComponentGraderHandler,
} from '@okrapdf/component';
import { receiptAssertionHandlers } from '@okrapdf/component/receipt';
import { Data, DateTime, Effect, Layer, Redacted, Result, Schedule, Schema } from 'effect';
import {
  FetchHttpClient,
  HttpClient,
  HttpClientError,
  HttpClientRequest,
  HttpRouter,
  HttpServer,
} from 'effect/unstable/http';
import { HttpApiBuilder, HttpApiSchema } from 'effect/unstable/httpapi';
import {
  existsSync,
  mkdirSync,
  readdirSync,
  readFileSync,
  realpathSync,
  statSync,
  writeFileSync,
} from 'fs';
import { Hono, type Context } from 'hono';
import { homedir } from 'os';
import { extname, join, resolve, sep } from 'path';
import {
  CLIENT_PROTOCOL_ENDPOINTS,
  CLIENT_PROTOCOL_HEADER,
  CLIENT_PROTOCOL_VERSION,
  apiBboxToClient,
  clientArtifactsSchema,
  clientDocumentParseSchema,
  clientDocumentSchema,
  clientLayoutBlockSchema,
  clientParseRequestSchema,
  clientParserSpecSchema,
  clientProtocolEventSchema,
  clientProviderSpecSchema,
  clientRedactionDetectionSchema,
  clientRunSchema,
  type ClientArtifacts,
  type ClientDocument,
  type ClientDocumentParse,
  type ClientLayoutBlock,
  type ClientPageLifecycle,
  type ClientPageParse,
  type ClientProtocolEvent,
  type ClientRedactionDetection,
  type ClientRun,
} from '@okrapdf/schemas/client-protocol';
import { validateSelfHostBundle, type SelfHostValidationResult } from './self-host';
import {
  toPromiseStore,
  type ServePgPoolFactory,
  type ServeStore,
  type StarterDocumentGraph,
} from './serve-store.js';
import {
  resolveEffectServeStore,
  ServeStoreError,
  ServeStoreService,
  type EffectServeStore,
} from './serve-store-effect.js';
import {
  layerFromOptions as serveRuntimeConfigLayer,
  layerJsonFiles,
  resolveServeRuntimeEnv,
  ServeJsonFiles,
  ServeIoError,
  ServeRuntimeConfig,
} from './serve-runtime-effect.js';
import {
  getSelfHostOpenApiSpec,
  makeSelfHostApiRegistry,
  SelfHostApiError,
  SelfHostCapabilityCatalog,
  SelfHostCapabilityRunSchema,
  SelfHostCoreApi,
  SelfHostDynamicWorkflowRunSchema,
  SelfHostHealthSchema,
  SelfHostJsonObject,
  SelfHostRuntimeStatusSchema,
  SelfHostWorkflowCatalogSchema,
  SelfHostWorkflowRunSchema,
} from './self-host-api';
import { isSelfHostResourceId } from './self-host-resource-id.js';
import { localAudit, localParse, localRedact } from './local-capabilities.js';
import { runNodeLocalParseProgram } from './serve-parse-runtime.js';
import { WORKBENCH_INDEX_HTML } from './workbench-ui.js';
import { nodeHttpClientLayer, nodeServicesLayer } from '../node-platform.js';
import {
  prepareDynamicWorkflowEffect,
  type DynamicWorkflowDefinition,
  type DynamicWorkflowStep,
} from '../../workflows.js';
import { validateStructuredDataEffect } from '../../structured-schema.js';

export interface SelfHostRuntimeRecipeSummary {
  id: string;
  name: string;
  version?: string;
  sourcePath: string;
  capabilityRefs: string[];
  graphReads: string[];
  graphWrites: string[];
  steps: SelfHostRuntimeRecipeStepSummary[];
}

export interface SelfHostRuntimeRecipeStepSummary {
  id: string;
  name: string;
  uses: string;
  reads: string[];
  writes: string[];
  requiresReview: boolean;
}

export interface SelfHostRuntimeCapabilitySummary {
  id: string;
  kind: string;
  name: string;
  sourcePath: string;
  namespace: string;
  networkNamespace: string;
}

export interface SelfHostRuntimeWorkflowExportSummary {
  name: string;
  sourcePath: string;
  recipeId?: string;
  requiredEnv: string[];
}

export interface SelfHostRuntimeServiceSummary {
  id: string;
  name: string;
  kind: string;
  runtime: string;
  public: boolean;
  required: boolean;
  networkRefs: string[];
  capabilityRef?: string;
  recipeCatalogRef?: string;
}

export interface SelfHostRuntimeDockerNetworkSummary {
  id: string;
  name: string;
  kind: string;
  dockerName: string;
  internal: boolean;
  external: boolean;
  attachable: boolean;
  capabilityRefs: string[];
}

export interface SelfHostRuntimeStatus {
  object: 'self_host_runtime_status';
  ok: boolean;
  root: string;
  schemaVersion?: string;
  uiRuntime?: string;
  apiRuntime?: string;
  auth: SelfHostRuntimeAuthStatus;
  deploymentTargets: string[];
  validation: SelfHostValidationResult;
  services: SelfHostRuntimeServiceSummary[];
  dockerNetworks: SelfHostRuntimeDockerNetworkSummary[];
  recipes: SelfHostRuntimeRecipeSummary[];
  capabilities: SelfHostRuntimeCapabilitySummary[];
  n8nWorkflows: SelfHostRuntimeWorkflowExportSummary[];
  endpoints: string[];
}

export interface SelfHostRuntimeRequestOptions {
  bundleDir: string;
  dataDir?: string;
  env?: Record<string, string | undefined>;
  publicDir?: string;
  capabilityFetch?: typeof fetch;
  /** Zero-config runtime used by `npx @okrapdf/cli web` without a bundle. */
  builtin?: boolean;
  /** Deterministic parser seam for conformance tests and embedders. */
  clientParse?: typeof localParse;
  /** Structural Postgres pool seam for tests and embedders; `pg` stays optional. */
  postgresPoolFactory?: ServePgPoolFactory;
  /** Additional Effect evaluators for Component-local grader and assertion ids. */
  componentEvaluators?: Readonly<Record<string, ComponentGraderHandler>>;
}

export interface SelfHostRuntimeServerOptions extends SelfHostRuntimeRequestOptions {
  host?: string;
  port?: number;
}

export interface StartedSelfHostRuntimeServer {
  server: Server;
  url: string;
  close: () => Promise<void>;
}

export interface SelfHostEffectHandler {
  handler: (request: Request) => Promise<Response>;
  dispose: () => Promise<void>;
}

export class SelfHostRuntimeRequestError extends Data.TaggedError('SelfHostRuntimeRequestError')<{
  readonly operation: 'create-app' | 'request';
  readonly message: string;
}> {}

const selfHostRuntimeRequestErrorCauses = new WeakMap<SelfHostRuntimeRequestError, unknown>();

function makeSelfHostRuntimeRequestError(
  operation: SelfHostRuntimeRequestError['operation'],
  cause: unknown,
): SelfHostRuntimeRequestError {
  const error = new SelfHostRuntimeRequestError({
    operation,
    message: 'Self-host runtime request failed',
  });
  selfHostRuntimeRequestErrorCauses.set(error, cause);
  return error;
}

export function selfHostRuntimeRequestErrorCause(error: SelfHostRuntimeRequestError): unknown {
  return selfHostRuntimeRequestErrorCauses.get(error);
}

interface StarterSourceRef {
  object: 'source_ref';
  id: string;
  kind: 'local_upload' | 'url' | 'n8n_webhook';
  status: 'accepted';
  received_at: string;
  filename?: string;
  uri?: string;
  content_type?: string;
  size_bytes?: number;
  sha256?: string;
  payload_keys?: string[];
}

interface StarterWorkflowRun {
  object: 'workflow_run';
  id: string;
  status: 'succeeded';
  mode: 'self_host_starter' | 'external_http';
  recipe_id: string;
  source_id: string | null;
  document_id: string;
  graph_writes: string[];
  graph_url: string;
  review_url: string;
  message: string;
  capability_runs: StarterCapabilityRun[];
}

interface StarterCapabilityRun {
  object: 'capability_run';
  id: string;
  workflow_run_id: string;
  recipe_id: string;
  step_id: string;
  capability_id: string;
  status: 'succeeded';
  mode: 'self_host_starter' | 'external_http';
  graph_writes: string[];
  started_at: string;
  completed_at: string;
  output: Record<string, unknown>;
  endpoint_url?: string;
}

interface SelfHostRuntimeAuthConfig {
  authMode: string;
  registrationMode: string;
  apiKey?: string;
  serverUsername: string;
  serverPassword?: string;
}

interface SelfHostRuntimeAuthBootstrapStatus {
  mode: string;
  owner_email_env: string;
  owner_email_configured: boolean;
  api_key_env: string;
  registration_mode: string;
  protected_routes: string[];
  notes?: string;
}

interface SelfHostRuntimeAuthStatus {
  auth_mode: string;
  registration_mode: string;
  api_key_configured: boolean;
  browser_password_configured: boolean;
  first_owner_configured: boolean;
  bootstrap: SelfHostRuntimeAuthBootstrapStatus;
  protected_routes: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : [];
}

function readJson(path: string): unknown {
  return JSON.parse(readFileSync(path, 'utf8'));
}

function listJsonFiles(dir: string): string[] {
  return readdirSync(dir, { withFileTypes: true }).flatMap((entry) => {
    const entryPath = join(dir, entry.name);
    if (entry.isDirectory()) return listJsonFiles(entryPath);
    return entry.isFile() && entry.name.endsWith('.json') ? [entryPath] : [];
  });
}

function catalogPaths(
  runtime: Record<string, unknown>,
  key: 'recipe_catalogs' | 'capability_catalogs',
): string[] {
  const catalogs = Array.isArray(runtime[key]) ? runtime[key] : [];
  return catalogs.flatMap((catalog) => {
    if (!isRecord(catalog) || typeof catalog.path !== 'string') return [];
    return [catalog.path];
  });
}

function loadRuntimeManifest(root: string): Record<string, unknown> | null {
  const path = join(root, 'runtime.manifest.json');
  if (!existsSync(path)) return null;
  const manifest = readJson(path);
  return isRecord(manifest) ? manifest : null;
}

function recipeSteps(value: unknown): SelfHostRuntimeRecipeStepSummary[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((step) => {
    if (!isRecord(step) || typeof step.id !== 'string' || typeof step.uses !== 'string') {
      return [];
    }
    return [
      {
        id: step.id,
        name: typeof step.name === 'string' ? step.name : step.id,
        uses: step.uses,
        reads: stringArray(step.reads),
        writes: stringArray(step.writes),
        requiresReview: step.requires_review === true,
      },
    ];
  });
}

function loadRecipeSummaries(
  root: string,
  runtime: Record<string, unknown> | null,
): SelfHostRuntimeRecipeSummary[] {
  const paths = runtime ? catalogPaths(runtime, 'recipe_catalogs') : ['./recipes'];
  const recipes: SelfHostRuntimeRecipeSummary[] = [];

  for (const path of paths) {
    const catalogPath = resolve(root, path);
    if (!existsSync(catalogPath)) continue;
    const files = statSync(catalogPath).isDirectory() ? listJsonFiles(catalogPath) : [catalogPath];
    for (const file of files.filter((item) => item.endsWith('.recipe.json'))) {
      const recipe = readJson(file);
      if (!isRecord(recipe) || typeof recipe.id !== 'string') continue;
      const graphContract = isRecord(recipe.graph_contract) ? recipe.graph_contract : {};
      const graphReads = stringArray(recipe.graph_reads);
      const graphWrites = stringArray(recipe.graph_writes);
      const steps = recipeSteps(recipe.steps);
      recipes.push({
        id: recipe.id,
        name: typeof recipe.name === 'string' ? recipe.name : recipe.id,
        ...(typeof recipe.version === 'string' ? { version: recipe.version } : {}),
        sourcePath: file,
        capabilityRefs: stringArray(recipe.capability_refs),
        graphReads: graphReads.length ? graphReads : stringArray(graphContract.reads),
        graphWrites: graphWrites.length ? graphWrites : stringArray(graphContract.writes),
        steps,
      });
    }
  }

  return recipes.sort((a, b) => a.id.localeCompare(b.id));
}

function loadCapabilitySummaries(
  root: string,
  runtime: Record<string, unknown> | null,
): SelfHostRuntimeCapabilitySummary[] {
  const paths = runtime ? catalogPaths(runtime, 'capability_catalogs') : ['./capabilities'];
  const capabilities: SelfHostRuntimeCapabilitySummary[] = [];

  for (const path of paths) {
    const catalogPath = resolve(root, path);
    if (!existsSync(catalogPath)) continue;
    const files = statSync(catalogPath).isDirectory() ? listJsonFiles(catalogPath) : [catalogPath];
    for (const file of files.filter((item) => item.endsWith('.engine.json'))) {
      const capability = readJson(file);
      if (!isRecord(capability) || typeof capability.id !== 'string') continue;
      const isolation = isRecord(capability.isolation) ? capability.isolation : {};
      capabilities.push({
        id: capability.id,
        kind: typeof capability.kind === 'string' ? capability.kind : 'unknown',
        name: typeof capability.name === 'string' ? capability.name : capability.id,
        sourcePath: file,
        namespace: typeof isolation.namespace === 'string' ? isolation.namespace : '',
        networkNamespace:
          typeof isolation.network_namespace === 'string' ? isolation.network_namespace : '',
      });
    }
  }

  return capabilities.sort((a, b) => a.id.localeCompare(b.id));
}

function loadN8nWorkflowSummaries(
  root: string,
  runtime: Record<string, unknown> | null,
): SelfHostRuntimeWorkflowExportSummary[] {
  const catalogs = runtime && Array.isArray(runtime.recipe_catalogs) ? runtime.recipe_catalogs : [];
  const paths = catalogs.flatMap((catalog) => {
    if (!isRecord(catalog) || catalog.kind !== 'n8n_export' || typeof catalog.path !== 'string') {
      return [];
    }
    return [catalog.path];
  });
  const workflows: SelfHostRuntimeWorkflowExportSummary[] = [];

  for (const path of paths) {
    const catalogPath = resolve(root, path);
    if (!existsSync(catalogPath)) continue;
    const files = statSync(catalogPath).isDirectory() ? listJsonFiles(catalogPath) : [catalogPath];
    for (const file of files.filter((item) => item.endsWith('.workflow.json'))) {
      const workflow = readJson(file);
      if (!isRecord(workflow)) continue;
      const meta = isRecord(workflow.meta) ? workflow.meta : {};
      const okrapdf = isRecord(meta.okrapdf) ? meta.okrapdf : {};
      workflows.push({
        name: typeof workflow.name === 'string' ? workflow.name : file,
        sourcePath: file,
        ...(typeof okrapdf.recipe_id === 'string' ? { recipeId: okrapdf.recipe_id } : {}),
        requiredEnv: stringArray(okrapdf.required_env),
      });
    }
  }

  return workflows.sort((a, b) => a.name.localeCompare(b.name));
}

function loadRuntimeServiceSummaries(
  runtime: Record<string, unknown> | null,
): SelfHostRuntimeServiceSummary[] {
  const services = Array.isArray(runtime?.services) ? runtime.services : [];
  return services.flatMap((service) => {
    if (!isRecord(service) || typeof service.id !== 'string') return [];
    return [
      {
        id: service.id,
        name: typeof service.name === 'string' ? service.name : service.id,
        kind: typeof service.kind === 'string' ? service.kind : '',
        runtime: typeof service.runtime === 'string' ? service.runtime : '',
        public: service.public === true,
        required: service.required !== false,
        networkRefs: stringArray(service.network_refs),
        ...(typeof service.capability_ref === 'string'
          ? { capabilityRef: service.capability_ref }
          : {}),
        ...(typeof service.recipe_catalog_ref === 'string'
          ? { recipeCatalogRef: service.recipe_catalog_ref }
          : {}),
      },
    ];
  });
}

function loadRuntimeDockerNetworkSummaries(
  runtime: Record<string, unknown> | null,
): SelfHostRuntimeDockerNetworkSummary[] {
  const networks = Array.isArray(runtime?.docker_networks) ? runtime.docker_networks : [];
  return networks.flatMap((network) => {
    if (!isRecord(network) || typeof network.id !== 'string') return [];
    const docker = isRecord(network.docker) ? network.docker : {};
    return [
      {
        id: network.id,
        name: typeof network.name === 'string' ? network.name : network.id,
        kind: typeof network.kind === 'string' ? network.kind : '',
        dockerName: typeof docker.name === 'string' ? docker.name : network.id,
        internal: docker.internal === true,
        external: docker.external === true,
        attachable: docker.attachable === true,
        capabilityRefs: stringArray(network.capability_refs),
      },
    ];
  });
}

function resolveServeRuntimeOptions(
  options: SelfHostRuntimeRequestOptions,
): SelfHostRuntimeRequestOptions {
  return {
    ...options,
    env: resolveServeRuntimeEnv(options),
  };
}

function runtimeEnv(
  options?: Pick<SelfHostRuntimeRequestOptions, 'env'>,
): Record<string, string | undefined> {
  return options?.env ?? {};
}

function stringEnv(
  env: Record<string, string | undefined>,
  name: string,
  fallback: string,
): string {
  const value = env[name]?.trim();
  return value ? value : fallback;
}

function authConfigFromEnv(env: Record<string, string | undefined>): SelfHostRuntimeAuthConfig {
  const apiKey = env.OKRA_API_KEY?.trim();
  const serverPassword = env.OKRA_SERVER_PASSWORD?.trim();
  return {
    authMode: stringEnv(env, 'OKRA_AUTH_MODE', 'single_owner'),
    registrationMode: stringEnv(env, 'OKRA_REGISTRATION_MODE', 'invite_only'),
    serverUsername: stringEnv(env, 'OKRA_SERVER_USERNAME', 'okra'),
    ...(apiKey ? { apiKey } : {}),
    ...(serverPassword ? { serverPassword } : {}),
  };
}

function protectedRouteList(): string[] {
  return [
    'GET /document',
    'GET /document/:id',
    'GET /document/:id/download',
    'GET /run',
    'GET /run/:id',
    'GET /run/:id/artifacts',
    'GET /run/:id/events',
    'GET /global/event',
    'GET /v1/documents/:id/*',
    'GET /v1/runs/:id',
    'GET /v1/capability-runs/:id',
    'POST /document',
    'POST /document/:id/parse',
    'POST /document/:id/upload',
    'POST /document/:id/upload-url',
    'POST /run/:id/cancel',
    'POST /run/:id/resume',
    'POST /v1/sources/n8n',
    'POST /v1/workflows',
  ];
}

function authBootstrapFromRuntime(
  runtime: Record<string, unknown> | null,
  config: SelfHostRuntimeAuthConfig,
  env: Record<string, string | undefined>,
): SelfHostRuntimeAuthBootstrapStatus {
  const bootstrap = isRecord(runtime?.auth_bootstrap) ? runtime.auth_bootstrap : {};
  const ownerEmailEnv =
    typeof bootstrap.owner_email_env === 'string'
      ? bootstrap.owner_email_env
      : 'OKRA_FIRST_OWNER_EMAIL';
  const apiKeyEnv =
    typeof bootstrap.api_key_env === 'string' ? bootstrap.api_key_env : 'OKRA_API_KEY';
  const protectedRoutes = stringArray(bootstrap.protected_routes);
  return {
    mode: typeof bootstrap.mode === 'string' ? bootstrap.mode : 'single_owner_api_key',
    owner_email_env: ownerEmailEnv,
    owner_email_configured: Boolean(env[ownerEmailEnv]?.trim()),
    api_key_env: apiKeyEnv,
    registration_mode:
      typeof bootstrap.registration_mode === 'string'
        ? bootstrap.registration_mode
        : config.registrationMode,
    protected_routes: protectedRoutes.length ? protectedRoutes : protectedRouteList(),
    ...(typeof bootstrap.notes === 'string' ? { notes: bootstrap.notes } : {}),
  };
}

function authStatusFromConfig(
  config: SelfHostRuntimeAuthConfig,
  runtime: Record<string, unknown> | null,
  env: Record<string, string | undefined>,
): SelfHostRuntimeAuthStatus {
  const bootstrap = authBootstrapFromRuntime(runtime, config, env);
  return {
    auth_mode: config.authMode,
    registration_mode: config.registrationMode,
    api_key_configured: Boolean(config.apiKey),
    browser_password_configured: Boolean(config.serverPassword),
    first_owner_configured: bootstrap.owner_email_configured,
    bootstrap,
    protected_routes: config.serverPassword ? ['*'] : protectedRouteList(),
  };
}

export function isLoopbackHost(host: string): boolean {
  const normalized = host
    .trim()
    .toLowerCase()
    .replace(/^\[|\]$/g, '');
  return normalized === '127.0.0.1' || normalized === 'localhost' || normalized === '::1';
}

export function networkBindWarning(
  host: string,
  env: Record<string, string | undefined>,
): string | undefined {
  if (isLoopbackHost(host)) return undefined;
  if (env.OKRA_SERVER_PASSWORD?.trim()) return undefined;
  if (env.OKRA_API_KEY?.trim()) {
    return 'WARNING: network access has no browser password. OKRA_API_KEY protects document and run routes, but the workbench cannot send it. Set OKRA_SERVER_PASSWORD before sharing.';
  }
  return 'WARNING: network access is unprotected. Set OKRA_SERVER_PASSWORD before sharing.';
}

/**
 * Liveness/readiness probes stay open in every auth mode. Container
 * healthchecks, load balancers, and reverse proxies cannot send a browser
 * password, and these routes only report bundle validity and catalog counts.
 */
const OPEN_PROBE_ROUTES: ReadonlySet<string> = new Set(['/health', '/ready', '/global/health']);

function protectedByApiKey(pathname: string): boolean {
  if (
    pathname === '/document' ||
    pathname === '/run' ||
    pathname === '/global/event' ||
    pathname === '/v1/sources/n8n' ||
    pathname === '/v1/workflows'
  ) {
    return true;
  }
  return (
    pathname.startsWith('/document/') ||
    pathname.startsWith('/run/') ||
    pathname.startsWith('/v1/documents/') ||
    pathname.startsWith('/v1/runs/') ||
    pathname.startsWith('/v1/capability-runs/')
  );
}

function bearerToken(header: string | null): string | null {
  if (!header) return null;
  const match = header.match(/^Bearer\s+(.+)$/i);
  return match ? match[1].trim() : null;
}
function secretsEqual(candidate: string | null | undefined, expected: string): boolean {
  if (!candidate) return false;
  const left = createHash('sha256').update(candidate).digest();
  const right = createHash('sha256').update(expected).digest();
  return timingSafeEqual(left, right);
}

function hasRuntimeApiKey(request: Request, config: SelfHostRuntimeAuthConfig): boolean {
  if (!config.apiKey) return true;
  return (
    secretsEqual(bearerToken(request.headers.get('authorization')), config.apiKey) ||
    secretsEqual(request.headers.get('x-okra-api-key'), config.apiKey)
  );
}

function basicCredentials(header: string | null): { username: string; password: string } | null {
  if (!header) return null;
  const match = header.match(/^Basic\s+(.+)$/i);
  if (!match) return null;
  try {
    const decoded = Buffer.from(match[1], 'base64').toString('utf8');
    const separator = decoded.indexOf(':');
    if (separator < 0) return null;
    return { username: decoded.slice(0, separator), password: decoded.slice(separator + 1) };
  } catch {
    return null;
  }
}

function hasBrowserPassword(request: Request, config: SelfHostRuntimeAuthConfig): boolean {
  if (!config.serverPassword) return true;
  const credentials = basicCredentials(request.headers.get('authorization'));
  return Boolean(
    credentials &&
    secretsEqual(credentials.username, config.serverUsername) &&
    secretsEqual(credentials.password, config.serverPassword),
  );
}

function hasAnyRuntimeCredential(request: Request, config: SelfHostRuntimeAuthConfig): boolean {
  if (config.serverPassword && hasBrowserPassword(request, config)) return true;
  if (config.apiKey && hasRuntimeApiKey(request, config)) return true;
  return !config.serverPassword && !config.apiKey;
}

function unauthorizedResponse(config?: SelfHostRuntimeAuthConfig): Response {
  const browserPassword = Boolean(config?.serverPassword);
  const response = jsonResponse(
    {
      object: 'error',
      code: 'UNAUTHORIZED',
      error: 'unauthorized',
      message: browserPassword
        ? 'Missing or invalid okra server credential.'
        : 'Missing or invalid OKRA_API_KEY credential.',
    },
    401,
  );
  response.headers.set(
    'www-authenticate',
    browserPassword
      ? 'Basic realm="okraPDF local workbench", charset="UTF-8"'
      : 'Bearer realm="okra"',
  );
  return response;
}
export function createSelfHostRuntimeStatus(
  bundleDir: string,
  env: Record<string, string | undefined>,
  options: { builtin?: boolean } = {},
): SelfHostRuntimeStatus {
  const root = resolve(bundleDir);
  const runtime = loadRuntimeManifest(root);
  const validation = options.builtin
    ? {
        object: 'self_host_validation' as const,
        ok: true,
        root,
        manifest: 'builtin://okra.client.v1',
        summary: null,
        errors: [],
        warnings: [],
      }
    : validateSelfHostBundle(root);
  const auth = authConfigFromEnv(env);
  const builtinCapabilities: SelfHostRuntimeCapabilitySummary[] = options.builtin
    ? [
        {
          id: 'parser.text-layer',
          kind: 'parser',
          name: 'Local text + OCR',
          sourcePath: 'builtin://parser.text-layer',
          namespace: 'local',
          networkNamespace: 'loopback',
        },
      ]
    : [];
  const builtinRecipes: SelfHostRuntimeRecipeSummary[] = options.builtin
    ? [
        {
          id: 'recipe.parse-document',
          name: 'Parse document locally',
          version: '1',
          sourcePath: 'builtin://recipe.parse-document',
          capabilityRefs: ['parser.text-layer'],
          graphReads: ['source'],
          graphWrites: ['pages', 'blocks', 'artifacts'],
          steps: [
            {
              id: 'parse',
              name: 'Local text + OCR',
              uses: 'parser.text-layer',
              reads: ['source'],
              writes: ['pages', 'blocks', 'artifacts'],
              requiresReview: false,
            },
          ],
        },
      ]
    : [];

  return {
    object: 'self_host_runtime_status',
    ok: validation.ok,
    root,
    ...(options.builtin
      ? { schemaVersion: CLIENT_PROTOCOL_VERSION, uiRuntime: 'okra-workbench', apiRuntime: 'node' }
      : typeof runtime?.schema_version === 'string'
        ? { schemaVersion: runtime.schema_version }
        : {}),
    ...(!options.builtin && typeof runtime?.ui_runtime === 'string'
      ? { uiRuntime: runtime.ui_runtime }
      : {}),
    ...(!options.builtin && typeof runtime?.api_runtime === 'string'
      ? { apiRuntime: runtime.api_runtime }
      : {}),
    auth: authStatusFromConfig(auth, runtime, env),
    deploymentTargets: options.builtin
      ? ['local', 'lan', 'reverse_proxy']
      : stringArray(runtime?.deployment_targets),
    validation,
    services: loadRuntimeServiceSummaries(runtime),
    dockerNetworks: loadRuntimeDockerNetworkSummaries(runtime),
    recipes: options.builtin ? builtinRecipes : loadRecipeSummaries(root, runtime),
    capabilities: options.builtin ? builtinCapabilities : loadCapabilitySummaries(root, runtime),
    n8nWorkflows: loadN8nWorkflowSummaries(root, runtime),
    endpoints: [
      'GET /health',
      'GET /openapi.json',
      'GET /v1/self-host/status',
      'GET /v1/runtime/manifest',
      'GET /v1/workflows/catalog',
      'GET /v1/capabilities',
      'GET /v1/runs/:id',
      'GET /v1/capability-runs/:id',
      'POST /document/:id/upload',
      'POST /document/:id/upload-url',
      'GET /document/:id/status',
      'GET /document/:id/full.md',
      'GET /document/:id/pages',
      'GET /document/:id/nodes',
      'GET /v1/documents/:id/graph',
      'GET /v1/documents/:id/reviews',
      'POST /v1/sources/n8n',
      'POST /v1/workflows',
      ...CLIENT_PROTOCOL_ENDPOINTS.map((endpoint) => `${endpoint.method} ${endpoint.path}`),
    ],
  };
}

function jsonResponse(value: unknown, status = 200): Response {
  return new Response(JSON.stringify(value), {
    status,
    headers: {
      'content-type': 'application/json; charset=utf-8',
      'cache-control': 'no-store',
    },
  });
}

function clientJsonResponse(value: unknown, status = 200): Response {
  const response = jsonResponse(value, status);
  response.headers.set(CLIENT_PROTOCOL_HEADER, CLIENT_PROTOCOL_VERSION);
  return response;
}

function textResponse(
  value: string,
  status = 200,
  contentType = 'text/plain; charset=utf-8',
): Response {
  return new Response(value, {
    status,
    headers: {
      'content-type': contentType,
      'cache-control': 'no-store',
    },
  });
}

function mimeType(path: string): string {
  switch (extname(path).toLowerCase()) {
    case '.css':
      return 'text/css; charset=utf-8';
    case '.html':
      return 'text/html; charset=utf-8';
    case '.js':
    case '.mjs':
      return 'text/javascript; charset=utf-8';
    case '.json':
    case '.map':
      return 'application/json; charset=utf-8';
    case '.webmanifest':
      return 'application/manifest+json; charset=utf-8';
    case '.txt':
      return 'text/plain; charset=utf-8';
    case '.svg':
      return 'image/svg+xml';
    case '.png':
      return 'image/png';
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg';
    case '.gif':
      return 'image/gif';
    case '.webp':
      return 'image/webp';
    case '.ico':
      return 'image/x-icon';
    case '.woff':
      return 'font/woff';
    case '.woff2':
      return 'font/woff2';
    case '.ttf':
      return 'font/ttf';
    case '.wasm':
      return 'application/wasm';
    case '.pdf':
      return 'application/pdf';
    default:
      return 'application/octet-stream';
  }
}

/** Static assets are bytes: a PNG or a woff2 read as UTF-8 text is corrupted. */
function staticFileResponse(path: string): Response {
  return new Response(new Uint8Array(readFileSync(path)), {
    status: 200,
    headers: { 'content-type': mimeType(path) },
  });
}

function resolveStaticPath(root: string, pathname: string): string | null {
  const publicRoot = resolve(root);
  let decoded: string;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    return null;
  }
  const relativePath = decoded === '/' ? 'index.html' : decoded.replace(/^\/+/, '');
  const candidate = resolve(publicRoot, relativePath);
  const insideRoot = candidate === publicRoot || candidate.startsWith(`${publicRoot}${sep}`);
  if (!insideRoot || !existsSync(candidate) || !statSync(candidate).isFile()) return null;
  return candidate;
}

function fallbackIndex(_status: SelfHostRuntimeStatus): string {
  return WORKBENCH_INDEX_HTML;
}

async function readRequestJson(request: Request): Promise<Record<string, unknown> | null> {
  const text = await request.text();
  if (!text.trim()) return {};
  try {
    const decoded = Schema.decodeUnknownResult(SelfHostJsonObject)(JSON.parse(text) as unknown);
    return Result.isSuccess(decoded) ? decoded.success : null;
  } catch {
    return null;
  }
}

async function withGraph(
  c: Context<SelfHostHonoEnv>,
  handler: (
    graph: StarterDocumentGraph,
    ctx: SelfHostRequestContext,
  ) => Response | Promise<Response>,
): Promise<Response> {
  const ctx = c.get('rc');
  const documentId = c.req.param('id')!;
  if (!isSelfHostResourceId(documentId)) {
    return jsonResponse({ object: 'error', error: 'invalid document id' }, 400);
  }
  const graph = await ctx.store.readGraph(documentId);
  if (!graph) {
    return jsonResponse({ object: 'error', error: `document "${documentId}" not found` }, 404);
  }
  return handler(graph, ctx);
}

function stableId(prefix: string, value: unknown): string {
  const hash = createHash('sha256').update(JSON.stringify(value)).digest('hex').slice(0, 16);
  return `${prefix}_${hash}`;
}

function sha256Hex(value: Uint8Array | string): string {
  return createHash('sha256').update(value).digest('hex');
}

function runtimeDataDir(root: string, options: SelfHostRuntimeRequestOptions): string {
  const env = runtimeEnv(options);
  return resolve(
    options.dataDir ??
      env.OKRA_DATA_DIR ??
      (options.builtin ? join(homedir(), '.okra', 'serve') : join(root, '.runtime-data')),
  );
}

function ensureDir(path: string): void {
  mkdirSync(path, { recursive: true });
}

function sourcePath(dataDir: string, sourceId: string): string {
  return join(dataDir, 'sources', `${sourceId}.json`);
}

function runPath(dataDir: string, runId: string): string {
  return join(dataDir, 'runs', `${runId}.json`);
}

function capabilityRunPath(dataDir: string, runId: string): string {
  return join(dataDir, 'capability-runs', `${runId}.json`);
}

function clientRunPath(dataDir: string, runId: string): string {
  return join(dataDir, 'client-runs', `${runId}.json`);
}

interface ClientRunRecord {
  object: 'client_run_record';
  run: ClientRun;
  events: ClientProtocolEvent[];
  artifacts?: ClientArtifacts;
  redaction?: ClientRedactionDetection;
  error?: string;
}

function readClientRunRecord(dataDir: string, runId: string): ClientRunRecord | null {
  if (!isSelfHostResourceId(runId)) return null;
  const path = clientRunPath(dataDir, runId);
  if (!existsSync(path)) return null;
  const value = readJson(path);
  if (!isRecord(value) || value.object !== 'client_run_record' || !isRecord(value.run)) return null;
  const run = clientRunSchema.safeParse(value.run);
  if (!run.success) return null;
  const artifacts = clientArtifactsSchema.safeParse(value.artifacts);
  const redaction = clientRedactionDetectionSchema.safeParse(value.redaction);
  const events = Array.isArray(value.events)
    ? value.events.flatMap((event) => {
        const parsed = clientProtocolEventSchema.safeParse(event);
        return parsed.success ? [parsed.data] : [];
      })
    : [];
  return {
    object: 'client_run_record',
    run: run.data,
    events,
    ...(artifacts.success ? { artifacts: artifacts.data } : {}),
    ...(redaction.success ? { redaction: redaction.data } : {}),
    ...(typeof value.error === 'string' ? { error: value.error } : {}),
  };
}

const clientPiiPatterns: ReadonlyArray<{
  readonly type: string;
  readonly pattern: RegExp;
}> = [
  { type: 'EMAIL_ADDRESS', pattern: /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g },
  { type: 'US_SSN', pattern: /\b\d{3}-\d{2}-\d{4}\b/g },
  { type: 'PHONE_NUMBER', pattern: /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g },
  { type: 'CREDIT_CARD', pattern: /\b(?:\d[ -]?){13,16}\b/g },
];

function detectClientPii(record: ClientRunRecord): ClientRedactionDetection {
  const candidates = (record.artifacts?.blocks ?? []).flatMap((block) =>
    clientPiiPatterns.flatMap(({ type, pattern }) =>
      Array.from(block.text.matchAll(pattern), (match) => ({
        id: stableId(
          'pii',
          `${record.run.id}:${block.page}:${type}:${match.index ?? 0}:${match[0]}`,
        ),
        type,
        text: match[0],
        score: 1,
        source: 'basic-policy',
        page: block.page,
        bbox: block.bbox,
      })),
    ),
  );
  return clientRedactionDetectionSchema.parse({
    object: 'client_redaction_detection',
    runId: record.run.id,
    engine: 'basic-policy',
    createdAt: new Date().toISOString(),
    candidates,
  });
}

function writeClientRunRecord(dataDir: string, record: ClientRunRecord): ClientRunRecord {
  writeJson(clientRunPath(dataDir, record.run.id), record);
  return record;
}

function listClientRunRecords(dataDir: string): ClientRunRecord[] {
  const dir = join(dataDir, 'client-runs');
  if (!existsSync(dir)) return [];
  return readdirSync(dir)
    .filter((name) => name.endsWith('.json'))
    .flatMap((name) => {
      const record = readClientRunRecord(dataDir, name.slice(0, -'.json'.length));
      return record ? [record] : [];
    })
    .sort((left, right) => right.run.createdAt.localeCompare(left.run.createdAt));
}

function decodedFilename(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function clientDocumentFromGraph(graph: StarterDocumentGraph): ClientDocument {
  const firstLineage = graph.lineage[0];
  return clientDocumentSchema.parse({
    object: 'client_document',
    id: graph.document.id,
    title: graph.document.filename ?? graph.document.id,
    source: `/document/${encodeURIComponent(graph.document.id)}/download`,
    pageCount: Math.max(1, graph.pages.length),
    openedAt:
      typeof firstLineage?.created_at === 'string'
        ? firstLineage.created_at
        : new Date(0).toISOString(),
  });
}

type ClientEventInput = ClientProtocolEvent extends infer Event
  ? Event extends ClientProtocolEvent
    ? Omit<Event, 'protocol' | 'id' | 'seq' | 'createdAt'>
    : never
  : never;

function clientEvent(value: ClientEventInput, seq: number): ClientProtocolEvent {
  const createdAt = new Date().toISOString();
  return clientProtocolEventSchema.parse({
    ...value,
    protocol: CLIENT_PROTOCOL_VERSION,
    id: `evt_${randomUUID().replaceAll('-', '')}`,
    seq,
    createdAt,
  });
}

interface ClientEventHub {
  publish: (event: ClientProtocolEvent) => void;
  subscribe: (listener: (event: ClientProtocolEvent) => void) => () => void;
}

function createClientEventHub(): ClientEventHub {
  const listeners = new Set<(event: ClientProtocolEvent) => void>();
  return {
    publish(event) {
      for (const listener of listeners) listener(event);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

function sseMessage(event: ClientProtocolEvent): string {
  return `id: ${event.id}\nevent: ${event.type}\ndata: ${JSON.stringify(event)}\n\n`;
}

function eventRunId(event: ClientProtocolEvent): string | null {
  if ('runId' in event) return event.runId;
  if ('run' in event) return event.run.id;
  return null;
}

function clientEventStream(
  request: Request,
  initial: ClientProtocolEvent[],
  hub: ClientEventHub,
  accepts: (event: ClientProtocolEvent) => boolean,
  once = false,
): Response {
  const encoder = new TextEncoder();
  let unsubscribe: (() => void) | undefined;
  let heartbeat: ReturnType<typeof setInterval> | undefined;
  const stream = new ReadableStream<Uint8Array>({
    start(controller) {
      for (const event of initial.filter(accepts))
        controller.enqueue(encoder.encode(sseMessage(event)));
      if (once) {
        controller.close();
        return;
      }
      const cleanup = () => {
        unsubscribe?.();
        if (heartbeat) clearInterval(heartbeat);
        try {
          controller.close();
        } catch {
          /* already closed */
        }
      };
      unsubscribe = hub.subscribe((event) => {
        if (accepts(event)) controller.enqueue(encoder.encode(sseMessage(event)));
      });
      heartbeat = setInterval(() => controller.enqueue(encoder.encode(': keep-alive\n\n')), 15_000);
      request.signal.addEventListener('abort', cleanup, { once: true });
    },
    cancel() {
      unsubscribe?.();
      if (heartbeat) clearInterval(heartbeat);
    },
  });
  return new Response(stream, {
    headers: {
      'content-type': 'text/event-stream; charset=utf-8',
      'cache-control': 'no-cache, no-transform',
      connection: 'keep-alive',
      [CLIENT_PROTOCOL_HEADER]: CLIENT_PROTOCOL_VERSION,
    },
  });
}

function writeJson(path: string, value: unknown): void {
  ensureDir(resolve(path, '..'));
  writeFileSync(path, `${JSON.stringify(value, null, 2)}\n`);
}

function readSource(dataDir: string, sourceId: string): StarterSourceRef | null {
  const path = sourcePath(dataDir, sourceId);
  if (!existsSync(path)) return null;
  const source = readJson(path);
  return isRecord(source) && source.object === 'source_ref'
    ? (source as unknown as StarterSourceRef)
    : null;
}

function writeSource(dataDir: string, source: StarterSourceRef): StarterSourceRef {
  writeJson(sourcePath(dataDir, source.id), source);
  return source;
}

function readWorkflowRun(dataDir: string, runId: string): StarterWorkflowRun | null {
  const path = runPath(dataDir, runId);
  if (!existsSync(path)) return null;
  const run = readJson(path);
  return isRecord(run) && run.object === 'workflow_run'
    ? (run as unknown as StarterWorkflowRun)
    : null;
}

function readCapabilityRun(dataDir: string, runId: string): StarterCapabilityRun | null {
  const path = capabilityRunPath(dataDir, runId);
  if (!existsSync(path)) return null;
  const run = readJson(path);
  return isRecord(run) && run.object === 'capability_run'
    ? (run as unknown as StarterCapabilityRun)
    : null;
}

function capabilityEnvName(capabilityId: string): string {
  return `OKRA_CAPABILITY_${capabilityId.toUpperCase().replace(/[^A-Z0-9]+/g, '_')}_URL`;
}

function capabilityEndpointMap(env: Record<string, string | undefined>): Map<string, string> {
  const endpoints = new Map<string, string>();
  const raw = env.OKRA_CAPABILITY_ENDPOINTS?.trim();
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (isRecord(parsed)) {
        for (const [capabilityId, endpoint] of Object.entries(parsed)) {
          if (typeof endpoint === 'string' && endpoint.trim()) {
            endpoints.set(capabilityId, endpoint.trim());
          }
        }
      }
    } catch {
      // Invalid endpoint maps are ignored here; deployment validation surfaces
      // static manifest issues, while runtime health should not leak secrets.
    }
  }
  return endpoints;
}

function capabilityEndpointFor(
  capabilityId: string,
  env: Record<string, string | undefined>,
): string | null {
  const perCapability = env[capabilityEnvName(capabilityId)]?.trim();
  if (perCapability) return perCapability;
  return capabilityEndpointMap(env).get(capabilityId) ?? null;
}

function capabilityProtocolUrl(endpoint: string): string {
  const url = new URL(endpoint);
  if (url.pathname === '/' || url.pathname === '') {
    url.pathname = '/v1/capability-runs';
  }
  return url.toString();
}

function mergeGraphArrayById(
  target: Array<Record<string, unknown>>,
  patch: unknown,
  runId: string,
  key: string,
): void {
  if (!Array.isArray(patch)) return;
  for (const item of patch) {
    if (!isRecord(item)) continue;
    const normalized: Record<string, unknown> = {
      ...item,
      ...(typeof item.created_by_run_id === 'string' ? {} : { created_by_run_id: runId }),
    };
    const identity = normalized[key];
    const index = target.findIndex((entry) => entry[key] === identity);
    if (typeof identity !== 'undefined' && index >= 0) {
      target[index] = { ...target[index], ...normalized };
    } else {
      target.push(normalized);
    }
  }
}

function applyGraphPatch(graph: StarterDocumentGraph, patch: unknown, runId: string): void {
  if (!isRecord(patch)) return;
  if (isRecord(patch.source)) graph.source = { ...graph.source, ...patch.source };
  if (isRecord(patch.document)) graph.document = { ...graph.document, ...patch.document };
  mergeGraphArrayById(graph.pages, patch.pages, runId, 'page_number');
  mergeGraphArrayById(graph.blocks, patch.blocks, runId, 'id');
  mergeGraphArrayById(graph.artifacts, patch.artifacts, runId, 'id');
  mergeGraphArrayById(graph.findings, patch.findings, runId, 'id');
  mergeGraphArrayById(graph.redactions, patch.redactions, runId, 'id');
  mergeGraphArrayById(graph.reviews, patch.reviews, runId, 'id');
}

function pageCountFromPdf(bytes: Uint8Array): number {
  const text = Buffer.from(bytes).toString('latin1');
  const matches = text.match(/\/Type\s*\/Page\b/g);
  return Math.max(1, matches?.length ?? 1);
}

function sourceToDocumentId(sourceId: string): string {
  return `doc-${sha256Hex(sourceId).slice(0, 20)}`;
}

function createStarterGraph(
  source: StarterSourceRef,
  documentId = sourceToDocumentId(source.id),
  options: { pageCount?: number; runId?: string } = {},
): StarterDocumentGraph {
  const now = new Date().toISOString();
  const pageCount = Math.max(1, options.pageCount ?? 1);
  const runId = options.runId ?? stableId('run_graph', { sourceId: source.id, documentId });
  const pages = Array.from({ length: pageCount }, (_, index) => ({
    page_number: index + 1,
    width: 612,
    height: 792,
    rendered_assets: [],
    text_state: source.kind === 'url' ? 'pending_ocr' : 'uploaded',
  }));
  const blocks = [
    {
      id: 'blk_source_summary',
      kind: 'paragraph',
      page_number: 1,
      text: [
        source.filename ? `File: ${source.filename}` : null,
        source.uri ? `Source: ${source.uri}` : null,
        `Ingested through ${source.kind}.`,
      ]
        .filter(Boolean)
        .join(' '),
      bbox: { x: 0.08, y: 0.08, w: 0.84, h: 0.08 },
      confidence: 1,
      created_by_run_id: runId,
    },
  ];
  const graph: StarterDocumentGraph = {
    schema_version: 'okra-document-graph/v1',
    source: {
      id: source.id,
      kind: source.kind,
      ...(source.uri ? { uri: source.uri } : {}),
      ...(source.filename ? { filename: source.filename } : {}),
      ...(source.content_type ? { content_type: source.content_type } : {}),
      ...(typeof source.size_bytes === 'number' ? { size_bytes: source.size_bytes } : {}),
      ...(source.sha256 ? { sha256: source.sha256 } : {}),
    },
    document: {
      id: documentId,
      source_id: source.id,
      ...(source.sha256 ? { sha256: source.sha256 } : {}),
      ...(source.filename ? { filename: source.filename } : {}),
      mime_type: source.content_type ?? 'application/pdf',
      lifecycle_state: 'ready',
      permissions: { access: 'private' },
    },
    pages,
    blocks,
    artifacts: [
      {
        id: 'art_document_graph',
        kind: 'json',
        path: `/v1/documents/${encodeURIComponent(documentId)}/graph`,
        media_type: 'application/json',
        created_by_run_id: runId,
      },
      ...(source.kind === 'local_upload'
        ? [
            {
              id: 'art_original_pdf',
              kind: 'original_pdf',
              path: `/document/${encodeURIComponent(documentId)}/download`,
              media_type: 'application/pdf',
              created_by_run_id: runId,
            },
          ]
        : []),
    ],
    findings: [],
    redactions: [],
    reviews: [],
    lineage: [
      {
        run_id: runId,
        engine_id: 'sourcer.local-upload',
        engine_version: '0.1.0',
        runtime: 'self_host_starter',
        params_hash: `sha256:${sha256Hex(JSON.stringify({ sourceId: source.id }))}`,
        input_hash: source.sha256 ? `sha256:${source.sha256}` : `sha256:${sha256Hex(source.id)}`,
        output_hash: `sha256:${sha256Hex(`${documentId}:${runId}`)}`,
        duration_ms: 0,
        cost: { amount: 0, currency: 'USD' },
        status: 'succeeded',
        created_at: now,
      },
    ],
  };

  return graph;
}

class CapabilityDispatchError extends Data.TaggedError('CapabilityDispatchError')<{
  readonly capabilityId: string;
  readonly message: string;
  readonly status: number;
  readonly retryable: boolean;
}> {}

function capabilityDispatchError(
  capabilityId: string,
  message: string,
  status = 502,
  retryable = status >= 500,
): CapabilityDispatchError {
  return new CapabilityDispatchError({ capabilityId, message, status, retryable });
}

function createCapabilityRunId(
  workflowRunId: string,
  recipeId: string,
  stepId: string,
  capabilityId: string,
): string {
  return stableId('caprun', {
    workflowRunId,
    recipeId,
    stepId,
    capabilityId,
  });
}

function appendLineage(
  graph: StarterDocumentGraph,
  run: StarterCapabilityRun,
  inputHash: string,
): void {
  // Idempotent: re-running the same verb on the same document reuses the stable
  // run id, so replace any existing lineage entry instead of duplicating it.
  const entry = {
    run_id: run.id,
    engine_id: run.capability_id,
    engine_version: '0.1.0',
    runtime: run.mode,
    params_hash: `sha256:${sha256Hex(JSON.stringify({ recipeId: run.recipe_id, stepId: run.step_id }))}`,
    input_hash: inputHash,
    output_hash: `sha256:${sha256Hex(JSON.stringify(run.output))}`,
    duration_ms: 0,
    cost: { amount: 0, currency: 'USD' },
    status: run.status,
    created_at: run.completed_at,
  };
  const existing = graph.lineage.findIndex((item) => item.run_id === entry.run_id);
  if (existing >= 0) graph.lineage[existing] = entry;
  else graph.lineage.push(entry);
}

async function applyStarterCapabilityStep(
  graph: StarterDocumentGraph,
  recipe: SelfHostRuntimeRecipeSummary,
  step: SelfHostRuntimeRecipeStepSummary,
  workflowRunId: string,
  store: ServeStore,
): Promise<StarterCapabilityRun> {
  const startedAt = new Date().toISOString();
  const capabilityRunId = createCapabilityRunId(workflowRunId, recipe.id, step.id, step.uses);
  const beforeHash = `sha256:${sha256Hex(
    JSON.stringify({
      document: graph.document,
      pages: graph.pages.length,
      blocks: graph.blocks.length,
      findings: graph.findings.length,
      redactions: graph.redactions.length,
      reviews: graph.reviews.length,
    }),
  )}`;
  const output: Record<string, unknown> = {
    writes: step.writes,
    capability_id: step.uses,
  };

  if (step.uses.startsWith('parser.')) {
    const parsed = await store.withMaterializedOriginal(
      graph.document.id,
      ({ dataDir, documentId }) =>
        localParse(dataDir, documentId, capabilityRunId, `blk_${step.id}_local`),
    );
    if (parsed.ok && parsed.blocks.length) {
      for (const block of parsed.blocks) {
        if (!graph.blocks.some((existing) => existing.id === block.id)) {
          graph.blocks.push(block);
        }
      }
      const textPages = new Set(parsed.blocks.map((block) => block.page_number as number));
      for (const page of graph.pages) {
        if (textPages.has(page.page_number as number)) {
          page.text_state = parsed.ocrUsed ? 'ocr' : 'text';
        }
      }
      output.blocks_written = parsed.blocks.length;
      output.mode = parsed.ocrUsed ? 'local_ocr' : 'local_text';
    } else {
      const blockId = `blk_${step.id}_starter`;
      if (!graph.blocks.some((block) => block.id === blockId)) {
        graph.blocks.push({
          id: blockId,
          kind: 'paragraph',
          page_number: 1,
          text: `Local PDF extraction unavailable (${parsed.reason ?? 'unknown'}). Install poppler-utils + tesseract-ocr, or set ${capabilityEnvName(step.uses)} to a parser service.`,
          bbox: { x: 0.08, y: 0.22, w: 0.84, h: 0.08 },
          confidence: 0.2,
          created_by_run_id: capabilityRunId,
        });
      }
      output.blocks_written = 1;
      output.mode = 'unavailable';
      output.reason = parsed.reason;
    }
  } else if (step.uses === 'auditor.wcag.basic') {
    const findings = localAudit(
      graph as unknown as Record<string, unknown>,
      capabilityRunId,
      `finding_${step.id}_starter`,
    );
    for (const finding of findings) {
      const existing = graph.findings.findIndex((entry) => entry.id === finding.id);
      if (existing >= 0) graph.findings[existing] = finding;
      else graph.findings.push(finding);
    }
    output.findings_written = findings.length;
  } else if (step.uses === 'redactor.policy.basic') {
    const redactions = localRedact(
      graph as unknown as Record<string, unknown>,
      capabilityRunId,
      `redaction_${step.id}_starter`,
    );
    for (const redaction of redactions) {
      const existing = graph.redactions.findIndex((entry) => entry.id === redaction.id);
      if (existing >= 0) graph.redactions[existing] = redaction;
      else graph.redactions.push(redaction);
    }
    output.redactions_written = redactions.length;
  } else if (step.uses === 'review.a11y.findings' || step.uses === 'review.redactions') {
    const reviewId = `review_${step.id}_starter`;
    if (!graph.reviews.some((review) => review.id === reviewId)) {
      graph.reviews.push({
        id: reviewId,
        kind: step.uses === 'review.redactions' ? 'redaction' : 'audit',
        status: 'pending',
        subject_type: step.uses === 'review.redactions' ? 'redaction' : 'finding',
        subject_id:
          step.uses === 'review.redactions'
            ? (graph.redactions[0]?.id ?? 'document')
            : (graph.findings[0]?.id ?? 'document'),
        reviewer_notes: `Starter review gate opened by ${step.uses}.`,
        final_claim_level: 'machine_only',
        created_by_run_id: capabilityRunId,
      });
    }
    output.reviews_written = 1;
  } else if (step.uses === 'viewer.document-graph') {
    output.viewer_url = `/?doc=${encodeURIComponent(graph.document.id)}&view=review`;
  } else if (step.uses === 'bridge.n8n.webhook') {
    output.source_id = graph.document.source_id;
  } else if (step.uses === 'sourcer.local-upload') {
    output.source_id = graph.document.source_id;
  }

  const run: StarterCapabilityRun = {
    object: 'capability_run',
    id: capabilityRunId,
    workflow_run_id: workflowRunId,
    recipe_id: recipe.id,
    step_id: step.id,
    capability_id: step.uses,
    status: 'succeeded',
    mode: 'self_host_starter',
    graph_writes: step.writes,
    started_at: startedAt,
    completed_at: new Date().toISOString(),
    output,
  };
  appendLineage(graph, run, beforeHash);
  return run;
}

function callExternalCapabilityStep(
  graph: StarterDocumentGraph,
  recipe: SelfHostRuntimeRecipeSummary,
  step: SelfHostRuntimeRecipeStepSummary,
  workflowRunId: string,
  endpoint: string,
  options: Pick<SelfHostRuntimeRequestOptions, 'capabilityFetch'>,
  parameters: Record<string, unknown>,
): Effect.Effect<StarterCapabilityRun, CapabilityDispatchError> {
  const capabilityRunId = createCapabilityRunId(workflowRunId, recipe.id, step.id, step.uses);
  const fetchImpl = options.capabilityFetch ?? fetch;

  const httpCause = (error: HttpClientError.HttpClientError): unknown =>
    'cause' in error.reason ? (error.reason.cause ?? error) : error;

  const attempt = Effect.gen(function* () {
    const startedAt = DateTime.formatIso(yield* DateTime.now);
    const endpointUrl = yield* Effect.try({
      try: () => capabilityProtocolUrl(endpoint),
      catch: (error) =>
        capabilityDispatchError(
          step.uses,
          `capability "${step.uses}" has an invalid endpoint URL: ${error instanceof Error ? error.message : String(error)}`,
          400,
          false,
        ),
    });
    const beforeHash = `sha256:${sha256Hex(
      JSON.stringify({
        document: graph.document,
        pages: graph.pages.length,
        blocks: graph.blocks.length,
        findings: graph.findings.length,
        redactions: graph.redactions.length,
        reviews: graph.reviews.length,
      }),
    )}`;

    const client = yield* HttpClient.HttpClient;
    const request = yield* HttpClientRequest.post(endpointUrl).pipe(
      HttpClientRequest.bodyJson({
        object: 'capability_run_request',
        protocol: 'okra-capability-http/v1',
        run_id: capabilityRunId,
        workflow_run_id: workflowRunId,
        recipe_id: recipe.id,
        document_id: graph.document.id,
        capability_id: step.uses,
        step: {
          id: step.id,
          name: step.name,
          reads: step.reads,
          writes: step.writes,
          requires_review: step.requiresReview,
        },
        parameters,
        graph,
      }),
      Effect.mapError((error) =>
        capabilityDispatchError(
          step.uses,
          `capability "${step.uses}" dispatch failed: ${error instanceof Error ? error.message : String(error)}`,
        ),
      ),
    );
    const response = yield* client.execute(request).pipe(
      Effect.mapError((error) => {
        const cause = httpCause(error);
        return capabilityDispatchError(
          step.uses,
          `capability "${step.uses}" dispatch failed: ${cause instanceof Error ? cause.message : String(cause)}`,
        );
      }),
      Effect.timeoutOrElse({
        duration: '60 seconds',
        orElse: () =>
          capabilityDispatchError(
            step.uses,
            `capability "${step.uses}" timed out after 60 seconds`,
          ),
      }),
    );

    if (response.status < 200 || response.status >= 300) {
      const message = yield* response.text.pipe(
        Effect.mapError(() =>
          capabilityDispatchError(
            step.uses,
            `capability "${step.uses}" returned HTTP ${response.status}`,
            response.status >= 500 ? 502 : 400,
            response.status >= 500,
          ),
        ),
      );
      return yield* capabilityDispatchError(
        step.uses,
        `capability "${step.uses}" returned HTTP ${response.status}${message ? `: ${message}` : ''}`,
        response.status >= 500 ? 502 : 400,
        response.status >= 500,
      );
    }

    const payload = yield* response.json.pipe(
      Effect.mapError(() =>
        capabilityDispatchError(
          step.uses,
          `capability "${step.uses}" returned an invalid JSON payload`,
          502,
          false,
        ),
      ),
    );
    if (!isRecord(payload)) {
      return yield* capabilityDispatchError(
        step.uses,
        `capability "${step.uses}" returned an invalid JSON payload`,
        502,
        false,
      );
    }
    if (payload.status === 'failed') {
      return yield* capabilityDispatchError(
        step.uses,
        `capability "${step.uses}" reported failure`,
        502,
        false,
      );
    }

    if (isRecord(payload.graph) && payload.graph.schema_version === 'okra-document-graph/v1') {
      Object.assign(graph, payload.graph);
    } else {
      applyGraphPatch(graph, payload.graph_patch, capabilityRunId);
    }

    const output = isRecord(payload.output)
      ? payload.output
      : { capability_id: step.uses, writes: step.writes };
    const completedAt = DateTime.formatIso(yield* DateTime.now);
    const run: StarterCapabilityRun = {
      object: 'capability_run',
      id: capabilityRunId,
      workflow_run_id: workflowRunId,
      recipe_id: recipe.id,
      step_id: step.id,
      capability_id: step.uses,
      status: 'succeeded',
      mode: 'external_http',
      graph_writes: step.writes,
      started_at: startedAt,
      completed_at: completedAt,
      endpoint_url: endpointUrl,
      output,
    };
    appendLineage(graph, run, beforeHash);
    return run;
  });

  return attempt.pipe(
    Effect.retry({
      times: 2,
      schedule: Schedule.exponential('100 millis'),
      while: (error) => error.retryable,
    }),
    Effect.provideService(FetchHttpClient.Fetch, fetchImpl),
    Effect.provideService(HttpClient.TracerPropagationEnabled, false),
    Effect.provide(nodeHttpClientLayer),
  );
}

function runStarterRecipe(
  graph: StarterDocumentGraph,
  recipe: SelfHostRuntimeRecipeSummary,
  workflowRunId: string,
  options: SelfHostRuntimeRequestOptions,
  parameters: Record<string, unknown>,
  store: ServeStore,
): Effect.Effect<StarterCapabilityRun[], CapabilityDispatchError> {
  const steps = recipe.steps.length
    ? recipe.steps
    : [
        {
          id: 'starter',
          name: 'Starter graph write',
          uses: recipe.capabilityRefs[0] ?? recipe.id,
          reads: recipe.graphReads,
          writes: recipe.graphWrites,
          requiresReview: false,
        },
      ];
  const env = runtimeEnv(options);
  return Effect.forEach(
    steps,
    (step) => {
      const endpoint = capabilityEndpointFor(step.uses, env);
      return endpoint
        ? callExternalCapabilityStep(
            graph,
            recipe,
            step,
            workflowRunId,
            endpoint,
            options,
            parameters,
          )
        : Effect.tryPromise({
            try: () => applyStarterCapabilityStep(graph, recipe, step, workflowRunId, store),
            catch: (cause) =>
              capabilityDispatchError(
                step.uses,
                `capability "${step.uses}" starter step failed: ${cause instanceof Error ? cause.message : String(cause)}`,
              ),
          });
    },
    { concurrency: 1 },
  );
}

function documentStatusFromGraph(graph: StarterDocumentGraph): Record<string, unknown> {
  return {
    phase: 'complete',
    pagesTotal: graph.pages.length,
    pagesCompleted: graph.pages.length,
    totalNodes: graph.blocks.length,
    verifiedNodes: graph.blocks.length,
    failedNodes: 0,
    pendingNodes: 0,
    document_id: graph.document.id,
    source_id: graph.source.id,
    graph_url: `/v1/documents/${encodeURIComponent(graph.document.id)}/graph`,
    plugins: graph.artifacts.map((artifact) => ({
      asset_id: artifact.id,
      kind: artifact.kind,
      status: 'ready',
      url: artifact.path,
    })),
  };
}

function markdownFromGraph(graph: StarterDocumentGraph): string {
  const lines = [
    `# ${graph.document.filename ?? graph.document.id}`,
    '',
    `Document ID: ${graph.document.id}`,
    `Source ID: ${graph.document.source_id}`,
    `Pages: ${graph.pages.length}`,
    '',
    '## Blocks',
    '',
    ...graph.blocks.map((block) => `- ${String(block.text ?? block.id)}`),
  ];

  if (graph.findings.length) {
    lines.push(
      '',
      '## Findings',
      '',
      ...graph.findings.map((finding) => `- ${String(finding.message ?? finding.id)}`),
    );
  }
  if (graph.redactions.length) {
    lines.push(
      '',
      '## Redactions',
      '',
      ...graph.redactions.map((redaction) => `- ${String(redaction.reason ?? redaction.id)}`),
    );
  }
  if (graph.reviews.length) {
    lines.push(
      '',
      '## Reviews',
      '',
      ...graph.reviews.map((review) => `- ${String(review.id)}: ${String(review.status)}`),
    );
  }

  return `${lines.join('\n')}\n`;
}

function pagesFromGraph(graph: StarterDocumentGraph): Array<Record<string, unknown>> {
  return graph.pages.map((page) => ({
    page: page.page_number,
    content: graph.blocks
      .filter((block) => block.page_number === page.page_number)
      .map((block) => String(block.text ?? ''))
      .join('\n\n'),
    blocks: graph.blocks
      .filter((block) => block.page_number === page.page_number)
      .map((block) => ({
        text: block.text ?? '',
        ...(isRecord(block.bbox)
          ? {
              bbox: {
                x: block.bbox.x,
                y: block.bbox.y,
                width: block.bbox.w,
                height: block.bbox.h,
              },
            }
          : {}),
        confidence: block.confidence,
      })),
    entities: [],
  }));
}

function entitiesFromGraph(graph: StarterDocumentGraph): Record<string, unknown> {
  const nodes = [
    ...graph.blocks.map((block) => ({
      id: block.id,
      type: block.kind,
      label: block.kind,
      value: block.text,
      page_number: block.page_number,
      status: 'ready',
      metadata: JSON.stringify({ created_by_run_id: block.created_by_run_id ?? null }),
    })),
    ...graph.findings.map((finding) => ({
      id: finding.id,
      type: 'finding',
      label: finding.severity,
      value: finding.message,
      page_number: 1,
      status: 'ready',
      metadata: JSON.stringify({ standard: finding.standard, rule_id: finding.rule_id }),
    })),
    ...graph.redactions.map((redaction) => ({
      id: redaction.id,
      type: 'redaction',
      label: redaction.state,
      value: redaction.reason,
      page_number: 1,
      status: 'ready',
      metadata: JSON.stringify({ regions: redaction.regions }),
    })),
  ];

  return { nodes, total: nodes.length, limit: nodes.length, offset: 0 };
}

function workflowCatalog(status: SelfHostRuntimeStatus): Record<string, unknown> {
  return {
    object: 'workflow_catalog',
    data: status.recipes.map((recipe) => ({
      id: recipe.id,
      name: recipe.name,
      version: recipe.version,
      capability_refs: recipe.capabilityRefs,
      graph_reads: recipe.graphReads,
      graph_writes: recipe.graphWrites,
      steps: recipe.steps.map((step) => ({
        id: step.id,
        uses: step.uses,
        reads: step.reads,
        writes: step.writes,
        requires_review: step.requiresReview,
      })),
    })),
  };
}

// ---------------------------------------------------------------------------
// HTTP server (Hono)
//
// The self-host runtime serves the SAME Web-standard Request/Response surface
// as the hosted cloud Worker (apps/api/server), so route signatures
// stay identical across deployments. Hono is the router: it runs on the
// Workers runtime in the cloud and on Node here via @hono/node-server.
// ---------------------------------------------------------------------------

interface SelfHostRequestContext {
  options: SelfHostRuntimeRequestOptions;
  root: string;
  env: Record<string, string | undefined>;
  status: SelfHostRuntimeStatus;
  dataDir: string;
  store: ServeStore;
  auth: SelfHostRuntimeAuthConfig;
}

type SelfHostHonoEnv = { Variables: { rc: SelfHostRequestContext } };

function serveStoreEnv(options: SelfHostRuntimeRequestOptions): Record<string, string | undefined> {
  return options.env ?? {};
}

function createEffectServeStore(options: SelfHostRuntimeRequestOptions): EffectServeStore {
  const root = resolve(options.bundleDir);
  return resolveEffectServeStore({
    dataDir: runtimeDataDir(root, options),
    env: serveStoreEnv(options),
    ...(options.postgresPoolFactory ? { poolFactory: options.postgresPoolFactory } : {}),
  });
}

function buildSelfHostContext(
  options: SelfHostRuntimeRequestOptions,
  store: ServeStore,
): SelfHostRequestContext {
  const root = resolve(options.bundleDir);
  const env = runtimeEnv(options);
  const status = createSelfHostRuntimeStatus(root, env, { builtin: options.builtin });
  const dataDir = runtimeDataDir(root, options);
  const auth = authConfigFromEnv(env);
  return { options, root, env, status, dataDir, store, auth };
}

function isServeTaggedError(
  error: unknown,
): error is ServeStoreError | ServeIoError | SelfHostApiError {
  return (
    error instanceof ServeStoreError ||
    error instanceof ServeIoError ||
    error instanceof SelfHostApiError
  );
}

function recoverServeTaggedError<A, E, R>(
  effect: Effect.Effect<A, E, R>,
): Effect.Effect<
  A,
  | Exclude<E, ServeStoreError | ServeIoError | SelfHostApiError>
  | { object: 'error'; error: string },
  R
> {
  return Effect.catchIf(effect, isServeTaggedError, (error) =>
    Effect.fail({ object: 'error' as const, error: error.message }),
  ) as Effect.Effect<
    A,
    | Exclude<E, ServeStoreError | ServeIoError | SelfHostApiError>
    | { object: 'error'; error: string },
    R
  >;
}

function effectHealth() {
  return Effect.gen(function* () {
    const config = yield* ServeRuntimeConfig;
    const status = createSelfHostRuntimeStatus(config.bundleDir, config.env, {
      builtin: config.builtin,
    });
    const payload = Schema.decodeUnknownSync(SelfHostHealthSchema)({
      object: 'self_host_health',
      ok: status.ok,
      ...(status.schemaVersion === undefined ? {} : { schema_version: status.schemaVersion }),
      recipes: status.recipes.length,
      capabilities: status.capabilities.length,
      n8n_workflows: status.n8nWorkflows.length,
    });
    return status.ok ? payload : yield* Effect.fail(payload);
  });
}

function effectDocumentGraph(documentId: string) {
  return Effect.gen(function* () {
    const store = yield* ServeStoreService;
    const graph = yield* store.readGraph(documentId);
    if (!graph) {
      return yield* Effect.fail({
        object: 'error' as const,
        error: `document "${documentId}" not found`,
      });
    }
    return graph;
  }).pipe(recoverServeTaggedError);
}

function effectOriginalPdf(documentId: string) {
  return effectDocumentGraph(documentId).pipe(
    Effect.flatMap(() =>
      Effect.gen(function* () {
        const store = yield* ServeStoreService;
        const bytes = yield* store.readOriginal(documentId);
        if (!bytes) {
          return yield* Effect.fail({
            object: 'error' as const,
            error: 'original PDF is not stored for this source',
          });
        }
        return bytes;
      }).pipe(recoverServeTaggedError),
    ),
  );
}

function writeSourceEffect(dataDir: string, source: StarterSourceRef) {
  return Effect.gen(function* () {
    const files = yield* ServeJsonFiles;
    yield* files.writeJson(sourcePath(dataDir, source.id), source);
    return source;
  });
}

function effectUploadUrl(documentId: string, uri: string) {
  return Effect.gen(function* () {
    const receivedAt = DateTime.formatIso(yield* DateTime.now);
    const config = yield* ServeRuntimeConfig;
    const store = yield* ServeStoreService;
    const dataDir =
      config.dataDir ??
      runtimeDataDir(resolve(config.bundleDir), {
        bundleDir: config.bundleDir,
        env: config.env,
        dataDir: config.dataDir,
      });
    const source = yield* writeSourceEffect(dataDir, {
      object: 'source_ref',
      id: stableId('src_url', { documentId, uri }),
      kind: 'url',
      status: 'accepted',
      received_at: receivedAt,
      uri,
      content_type: 'application/pdf',
    });
    const graph = yield* store.writeGraph(createStarterGraph(source, documentId));
    return {
      phase: 'complete' as const,
      document_id: documentId,
      source_id: source.id,
      graph_url: `/v1/documents/${encodeURIComponent(graph.document.id)}/graph`,
    };
  }).pipe(recoverServeTaggedError);
}

function effectUpload(
  documentId: string,
  body: Uint8Array,
  headers: { readonly 'content-type'?: string; readonly 'x-file-name'?: string },
) {
  if (body.byteLength === 0) {
    return Effect.fail({ object: 'error' as const, error: 'upload body is required' });
  }
  return Effect.gen(function* () {
    const receivedAt = DateTime.formatIso(yield* DateTime.now);
    const config = yield* ServeRuntimeConfig;
    const store = yield* ServeStoreService;
    const dataDir =
      config.dataDir ??
      runtimeDataDir(resolve(config.bundleDir), {
        bundleDir: config.bundleDir,
        env: config.env,
        dataDir: config.dataDir,
      });
    const hash = sha256Hex(body);
    const filename = headers['x-file-name'] ?? `${documentId}.pdf`;
    const source = yield* writeSourceEffect(dataDir, {
      object: 'source_ref',
      id: stableId('src_upload', { documentId, hash }),
      kind: 'local_upload',
      status: 'accepted',
      received_at: receivedAt,
      filename,
      content_type: headers['content-type'] ?? 'application/pdf',
      size_bytes: body.byteLength,
      sha256: hash,
    });
    yield* store.writeOriginal(documentId, body);
    const graph = yield* store.writeGraph(
      createStarterGraph(source, documentId, { pageCount: pageCountFromPdf(body) }),
    );
    return {
      phase: 'complete' as const,
      document_id: documentId,
      source_id: source.id,
      pagesTotal: graph.pages.length,
      graph_url: `/v1/documents/${encodeURIComponent(graph.document.id)}/graph`,
    };
  }).pipe(recoverServeTaggedError);
}

function effectN8nSource(body: Record<string, unknown>) {
  return Effect.gen(function* () {
    const receivedAt = DateTime.formatIso(yield* DateTime.now);
    const config = yield* ServeRuntimeConfig;
    const dataDir =
      config.dataDir ??
      runtimeDataDir(resolve(config.bundleDir), {
        bundleDir: config.bundleDir,
        env: config.env,
        dataDir: config.dataDir,
      });
    return yield* writeSourceEffect(dataDir, {
      object: 'source_ref',
      id: stableId('src_n8n', body),
      kind: 'n8n_webhook',
      status: 'accepted',
      received_at: receivedAt,
      uri: typeof body.document_url === 'string' ? body.document_url : undefined,
      payload_keys: Object.keys(body).sort(),
    });
  }).pipe(recoverServeTaggedError);
}

interface EffectWorkflowRunRequest {
  readonly recipe_id?: string;
  readonly recipeId?: string;
  readonly source_id?: string;
  readonly sourceId?: string;
  readonly document_id?: string;
  readonly documentId?: string;
  readonly parameters?: Readonly<Record<string, unknown>>;
}

interface EffectWorkflowResourceRequest {
  readonly name?: string;
  readonly definition: { readonly code: string };
  readonly run: boolean;
  readonly inputs?: Readonly<Record<string, unknown>>;
  readonly params?: Readonly<Record<string, unknown>>;
}

function isWorkflowResourceRequest(body: unknown): body is EffectWorkflowResourceRequest {
  if (!isRecord(body) || typeof body.run !== 'boolean' || !isRecord(body.definition)) {
    return false;
  }
  return typeof body.definition.code === 'string';
}

function workflowBadRequest(error: string) {
  return {
    object: 'error' as const,
    code: 'BAD_REQUEST' as const,
    error,
  };
}

function resolvedDataDir() {
  return Effect.gen(function* () {
    const config = yield* ServeRuntimeConfig;
    return (
      config.dataDir ??
      runtimeDataDir(resolve(config.bundleDir), {
        bundleDir: config.bundleDir,
        env: config.env,
        dataDir: config.dataDir,
      })
    );
  });
}

function readJsonRecordEffect(path: string) {
  return Effect.gen(function* () {
    const files = yield* ServeJsonFiles;
    const exists = yield* files.exists(path);
    if (!exists) return null;
    const value = yield* files.readJson(path);
    return isRecord(value) ? value : null;
  });
}

function effectWorkflowRun(
  options: SelfHostRuntimeRequestOptions,
  store: ServeStore,
  body: EffectWorkflowRunRequest,
) {
  return Effect.gen(function* () {
    const config = yield* ServeRuntimeConfig;
    const effectStore = yield* ServeStoreService;
    const files = yield* ServeJsonFiles;
    const dataDir = yield* resolvedDataDir();
    const status = createSelfHostRuntimeStatus(config.bundleDir, config.env, {
      builtin: config.builtin,
    });
    const recipeId = body.recipe_id ?? body.recipeId;
    if (!recipeId) return yield* Effect.fail(workflowBadRequest('recipe_id is required'));

    const recipe = status.recipes.find((item) => item.id === recipeId);
    if (!recipe) {
      return yield* Effect.fail(workflowBadRequest(`unknown recipe_id "${recipeId}"`));
    }

    const sourceId = body.source_id ?? body.sourceId;
    const documentIdFromBody = body.document_id ?? body.documentId;
    const existingGraph = documentIdFromBody
      ? yield* effectStore.readGraph(documentIdFromBody)
      : null;
    const sourceRecord = sourceId
      ? yield* readJsonRecordEffect(sourcePath(dataDir, sourceId))
      : null;
    const source =
      sourceRecord && sourceRecord.object === 'source_ref'
        ? (sourceRecord as unknown as StarterSourceRef)
        : null;
    const parameters = { ...(body.parameters ?? {}) };

    if (!existingGraph && !source) {
      return yield* Effect.fail(
        workflowBadRequest('workflow requires a known source_id or document_id'),
      );
    }

    const documentId =
      existingGraph?.document.id ?? documentIdFromBody ?? sourceToDocumentId(source!.id);
    const runId = stableId('run', {
      recipeId: recipe.id,
      sourceId: source?.id ?? null,
      documentId,
    });
    const resolvedGraph = existingGraph ?? (yield* effectStore.readGraph(documentId));
    const graph = resolvedGraph ?? createStarterGraph(source!, documentId, { runId });
    const capabilityRuns = yield* runStarterRecipe(
      graph,
      recipe,
      runId,
      options,
      parameters,
      store,
    ).pipe(
      Effect.mapError((error) => ({
        object: 'error' as const,
        code: 'CAPABILITY_DISPATCH_FAILED' as const,
        capability_id: error.capabilityId,
        error: error.message,
      })),
    );

    yield* effectStore.writeGraph(graph);
    for (const capabilityRun of capabilityRuns) {
      yield* files.writeJson(capabilityRunPath(dataDir, capabilityRun.id), capabilityRun);
    }
    const run: StarterWorkflowRun = {
      object: 'workflow_run',
      id: runId,
      status: 'succeeded',
      mode: capabilityRuns.some((capabilityRun) => capabilityRun.mode === 'external_http')
        ? 'external_http'
        : 'self_host_starter',
      recipe_id: recipe.id,
      source_id: source?.id ?? existingGraph?.document.source_id ?? null,
      document_id: graph.document.id,
      graph_writes: recipe.graphWrites,
      graph_url: `/v1/documents/${encodeURIComponent(graph.document.id)}/graph`,
      review_url: `/v1/documents/${encodeURIComponent(graph.document.id)}/reviews`,
      message: capabilityRuns.some((capabilityRun) => capabilityRun.mode === 'external_http')
        ? 'Executed by the lightweight self-host dispatcher with one or more external capability services.'
        : 'Executed by the lightweight self-host starter capability dispatcher. Replace these starter runs with isolated runtime services as capability images become real.',
      capability_runs: capabilityRuns,
    };
    const decoded = Schema.decodeUnknownSync(SelfHostWorkflowRunSchema)(run);
    yield* files.writeJson(runPath(dataDir, run.id), decoded);
    return decoded;
  }).pipe(
    Effect.mapError((error) => {
      if (isServeTaggedError(error)) {
        return {
          object: 'error' as const,
          code: 'INTERNAL_SERVER_ERROR' as const,
          error: error.message,
        };
      }
      return error as
        | { object: 'error'; code: 'BAD_REQUEST'; error: string }
        | {
            object: 'error';
            code: 'CAPABILITY_DISPATCH_FAILED';
            capability_id: string;
            error: string;
          }
        | { object: 'error'; code: 'INTERNAL_SERVER_ERROR'; error: string };
    }),
  );
}

function dynamicWorkflowCapabilityId(step: DynamicWorkflowStep): string {
  switch (step.kind) {
    case 'ocr':
      return `parser.${step.provider}`;
    case 'gate':
      return 'gate.runtime';
    case 'code':
      return `code.${step.runtime}`;
    case 'vlm':
      return `vlm.${step.model}`;
  }
}

function dynamicWorkflowDocumentId(payload: Readonly<Record<string, unknown>>): string | undefined {
  const inputs = isRecord(payload.inputs) ? payload.inputs : {};
  const candidate =
    inputs.document_id ?? inputs.documentId ?? payload.document_id ?? payload.documentId;
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : undefined;
}

function componentAssertionRequirements(definition: DynamicWorkflowDefinition) {
  const metadata = isRecord(definition.metadata) ? definition.metadata : {};
  const component = isRecord(metadata.component) ? metadata.component : {};
  return Schema.decodeUnknownSync(Schema.Array(ComponentAssertionRequirementSchema))(
    component.assertions ?? [],
  );
}

function effectDynamicWorkflowResource(
  options: SelfHostRuntimeRequestOptions,
  body: EffectWorkflowResourceRequest,
) {
  return Effect.gen(function* () {
    const payload = body.run ? body.params : body.inputs;
    if (!payload || !isRecord(payload.workflow)) {
      return yield* Effect.fail(
        workflowBadRequest(`${body.run ? 'params' : 'inputs'}.workflow is required`),
      );
    }

    const prepared = yield* prepareDynamicWorkflowEffect(
      payload.workflow as unknown as DynamicWorkflowDefinition,
    ).pipe(Effect.mapError((error) => workflowBadRequest(error.message)));
    const definition = prepared.definition;
    const createdAt = DateTime.formatIso(yield* DateTime.now);
    const workflowId = definition.id;
    const versionId = stableId('wfv', {
      workflow: definition,
      source: body.definition.code,
    });
    const resourceBase = {
      object: 'workflow' as const,
      id: workflowId,
      workflow_id: workflowId,
      name: body.name ?? definition.name ?? null,
      version_id: versionId,
      execution_mode: 'agent_workflow_script' as const,
      runnable: true,
      created_at: createdAt,
    };

    if (!body.run) {
      return { ...resourceBase, run: null };
    }

    const documentId = dynamicWorkflowDocumentId(payload);
    if (!documentId) {
      return yield* Effect.fail(
        workflowBadRequest('params.inputs.document_id is required for self-host execution'),
      );
    }
    const store = yield* ServeStoreService;
    const graph = yield* store.readGraph(documentId);
    if (!graph) {
      return yield* Effect.fail(workflowBadRequest(`document "${documentId}" not found`));
    }

    const env = runtimeEnv(options);
    const runId = stableId('run', { workflowId, versionId, documentId, payload });
    const outputs: Record<string, unknown> = {};
    const capabilityRuns = yield* Effect.forEach(definition.steps, (step) => {
      const capabilityId = dynamicWorkflowCapabilityId(step);
      const endpoint = capabilityEndpointFor(capabilityId, env);
      if (!endpoint) {
        return Effect.fail(
          capabilityDispatchError(
            capabilityId,
            `${capabilityEnvName(capabilityId)} is required to run step "${step.id}"`,
            503,
            false,
          ),
        );
      }
      const recipe: SelfHostRuntimeRecipeSummary = {
        id: definition.id,
        name: definition.name,
        sourcePath: 'dynamic-workflow',
        capabilityRefs: [capabilityId],
        graphReads: ['document', 'pages', 'blocks', 'artifacts'],
        graphWrites: ['artifacts'],
        steps: [],
      };
      const runtimeStep: SelfHostRuntimeRecipeStepSummary = {
        id: step.id,
        name: step.label ?? step.id,
        uses: capabilityId,
        reads: recipe.graphReads,
        writes: recipe.graphWrites,
        requiresReview: false,
      };
      return callExternalCapabilityStep(
        graph,
        recipe,
        runtimeStep,
        runId,
        endpoint,
        options,
        {
          workflow: definition,
          step,
          inputs: isRecord(payload.inputs) ? payload.inputs : {},
          prior_outputs: { ...outputs },
        },
      ).pipe(
        Effect.flatMap((capabilityRun) => {
          const validate =
            step.kind === 'vlm' && step.schema
              ? validateStructuredDataEffect(step.schema, capabilityRun.output)
              : Effect.succeed({ success: true as const, data: capabilityRun.output });
          return validate.pipe(
            Effect.mapError(() =>
              capabilityDispatchError(
                capabilityId,
                `step "${step.id}" returned output that could not be validated`,
                502,
                false,
              ),
            ),
            Effect.flatMap((validation) =>
              validation.success
                ? Effect.sync(() => {
                    outputs[step.id] = validation.data;
                    return capabilityRun;
                  })
                : Effect.fail(
                    capabilityDispatchError(
                      capabilityId,
                      `step "${step.id}" returned output outside its structured schema: ${validation.issues.join('; ')}`,
                      502,
                      false,
                    ),
                  ),
            ),
          );
        }),
      );
    });

    const requirements = yield* Effect.try({
      try: () => componentAssertionRequirements(definition),
      catch: () => workflowBadRequest('workflow component assertions are invalid'),
    });
    const assertions =
      requirements.length === 0
        ? []
        : yield* runComponentAssertionRequirementsEffect(requirements, outputs).pipe(
            Effect.provide(
              layerComponentGraders({
                ...standardComponentEvaluatorHandlers,
                ...receiptAssertionHandlers,
                ...(options.componentEvaluators ?? {}),
              }),
            ),
            Effect.mapError((error) => ({
              object: 'error' as const,
              code: 'INTERNAL_SERVER_ERROR' as const,
              error: `component evaluator "${error.evaluator}" is unavailable or failed`,
            })),
          );
    const succeeded = assertions.every((assertion) => assertion.passed);
    const completedAt = DateTime.formatIso(yield* DateTime.now);
    const output = Schema.decodeUnknownSync(SelfHostJsonObject)({
      outputs,
      assertions,
      capability_runs: capabilityRuns,
      eval: definition.eval ?? null,
    });
    const run = Schema.decodeUnknownSync(SelfHostDynamicWorkflowRunSchema)({
      object: 'run',
      id: runId,
      run_id: runId,
      workflow_id: workflowId,
      status: succeeded ? 'succeeded' : 'failed',
      status_url: `/v1/runs/${encodeURIComponent(runId)}`,
      output,
      error: succeeded ? null : 'One or more Component assertions failed.',
      created_at: createdAt,
      updated_at: completedAt,
    });

    yield* store.writeGraph(graph);
    const files = yield* ServeJsonFiles;
    for (const capabilityRun of capabilityRuns) {
      yield* files.writeJson(capabilityRunPath(yield* resolvedDataDir(), capabilityRun.id), capabilityRun);
    }
    yield* files.writeJson(runPath(yield* resolvedDataDir(), run.id), run);
    return { ...resourceBase, run };
  }).pipe(
    Effect.mapError((error) => {
      if (error instanceof CapabilityDispatchError) {
        if (error.status === 503) {
          return {
            object: 'error' as const,
            code: 'RUNTIME_UNAVAILABLE' as const,
            error: error.message,
          };
        }
        return {
          object: 'error' as const,
          code: 'CAPABILITY_DISPATCH_FAILED' as const,
          capability_id: error.capabilityId,
          error: error.message,
        };
      }
      if (isServeTaggedError(error)) {
        return {
          object: 'error' as const,
          code: 'INTERNAL_SERVER_ERROR' as const,
          error: error.message,
        };
      }
      return error as
        | { object: 'error'; code: 'BAD_REQUEST'; error: string }
        | { object: 'error'; code: 'RUNTIME_UNAVAILABLE'; error: string }
        | {
            object: 'error';
            code: 'CAPABILITY_DISPATCH_FAILED';
            capability_id: string;
            error: string;
          }
        | { object: 'error'; code: 'INTERNAL_SERVER_ERROR'; error: string };
    }),
  );
}

function effectWorkflowPost(
  options: SelfHostRuntimeRequestOptions,
  store: ServeStore,
  body: unknown,
) {
  return isWorkflowResourceRequest(body)
    ? effectDynamicWorkflowResource(options, body)
    : effectWorkflowRun(options, store, body as EffectWorkflowRunRequest);
}

function effectReadWorkflowRun(runId: string) {
  return Effect.gen(function* () {
    const dataDir = yield* resolvedDataDir();
    const run = yield* readJsonRecordEffect(runPath(dataDir, runId));
    if (!run || (run.object !== 'workflow_run' && run.object !== 'run')) {
      return yield* Effect.fail({ object: 'error' as const, error: 'workflow run not found' });
    }
    return run.object === 'run'
      ? Schema.decodeUnknownSync(SelfHostDynamicWorkflowRunSchema)(run)
      : Schema.decodeUnknownSync(SelfHostWorkflowRunSchema)(run);
  }).pipe(recoverServeTaggedError);
}

function effectReadCapabilityRun(runId: string) {
  return Effect.gen(function* () {
    const dataDir = yield* resolvedDataDir();
    const run = yield* readJsonRecordEffect(capabilityRunPath(dataDir, runId));
    if (!run || run.object !== 'capability_run') {
      return yield* Effect.fail({ object: 'error' as const, error: 'capability run not found' });
    }
    return Schema.decodeUnknownSync(SelfHostCapabilityRunSchema)(run);
  }).pipe(recoverServeTaggedError);
}

/**
 * Build the Fetch-compatible Effect runtime for the API groups that have
 * completed the HttpApiBuilder migration. Hono delegates these routes to this
 * handler and owns only transport/static compatibility around them.
 */
function createSelfHostEffectHandlerWithStore(
  options: SelfHostRuntimeRequestOptions,
  effectStore: EffectServeStore,
  store: ServeStore,
): SelfHostEffectHandler {
  const resolved = resolveServeRuntimeOptions(options);
  const root = resolve(resolved.bundleDir);
  const dataDir = runtimeDataDir(root, resolved);
  const env = runtimeEnv(resolved);
  const authConfig = authConfigFromEnv(env);
  const runtimeLayers = Layer.mergeAll(
    serveRuntimeConfigLayer({
      env,
      bundleDir: resolved.bundleDir,
      dataDir,
      ...(resolved.builtin === undefined ? {} : { builtin: resolved.builtin }),
      ...(resolved.publicDir === undefined ? {} : { publicDir: resolved.publicDir }),
    }),
    Layer.succeed(ServeStoreService, effectStore),
    layerJsonFiles().pipe(Layer.provide(nodeServicesLayer)),
  );
  type AuthorizationService = Parameters<typeof Authorization.of>[0];
  const authorizationFailure = (message: string, challenge: string) =>
    Effect.fail(
      HttpApiSchema.withHeaders({
        body: {
          object: 'error' as const,
          code: 'UNAUTHORIZED' as const,
          error: 'unauthorized' as const,
          message,
        },
        headers: { 'www-authenticate': challenge },
      }),
    );
  const authorizeToken: AuthorizationService['bearer'] = (httpEffect, { credential }) => {
    if (
      (authConfig.apiKey && secretsEqual(Redacted.value(credential), authConfig.apiKey)) ||
      (!authConfig.apiKey && !authConfig.serverPassword)
    ) {
      return httpEffect;
    }
    return authorizationFailure(
      'Missing or invalid OKRA_API_KEY credential.',
      'Bearer realm="okra"',
    );
  };
  const authorizeBasic: AuthorizationService['basic'] = (httpEffect, { credential }) => {
    if (
      (authConfig.serverPassword &&
        secretsEqual(credential.username, authConfig.serverUsername) &&
        secretsEqual(Redacted.value(credential.password), authConfig.serverPassword)) ||
      (!authConfig.serverPassword && !authConfig.apiKey)
    ) {
      return httpEffect;
    }
    return authorizationFailure(
      'Missing or invalid okra server credential.',
      'Basic realm="okraPDF local workbench", charset="UTF-8"',
    );
  };
  const authorization = Layer.succeed(
    Authorization,
    Authorization.of({
      bearer: authorizeToken,
      apiKey: authorizeToken,
      basic: authorizeBasic,
    }),
  );
  const systemHandlers = HttpApiBuilder.group(SelfHostCoreApi, 'system', (handlers) =>
    handlers
      .handle('health', () => effectHealth())
      .handle('ready', () => effectHealth())
      .handle('openapi', () =>
        Effect.sync(() => Schema.decodeUnknownSync(SelfHostJsonObject)(getSelfHostOpenApiSpec())),
      ),
  ).pipe(Layer.provide(runtimeLayers));

  const runtimeHandlers = HttpApiBuilder.group(SelfHostCoreApi, 'runtime', (handlers) =>
    handlers
      .handle('status', () =>
        Effect.gen(function* () {
          const config = yield* ServeRuntimeConfig;
          const status = createSelfHostRuntimeStatus(config.bundleDir, config.env, {
            builtin: config.builtin,
          });
          const payload = Schema.decodeUnknownSync(SelfHostRuntimeStatusSchema)(
            JSON.parse(JSON.stringify(status)),
          );
          return status.ok ? payload : yield* Effect.fail(payload);
        }),
      )
      .handle('manifest', () =>
        Effect.gen(function* () {
          const config = yield* ServeRuntimeConfig;
          const files = yield* ServeJsonFiles;
          const manifestPath = join(resolve(config.bundleDir), 'runtime.manifest.json');
          const exists = yield* files.exists(manifestPath);
          if (!exists) {
            return yield* Effect.fail({
              object: 'error' as const,
              error: 'runtime manifest not found',
            });
          }
          const manifest = yield* files.readJson(manifestPath);
          return Schema.decodeUnknownSync(SelfHostJsonObject)(manifest);
        }).pipe(recoverServeTaggedError),
      )
      .handle('capabilities', () =>
        Effect.gen(function* () {
          const config = yield* ServeRuntimeConfig;
          const status = createSelfHostRuntimeStatus(config.bundleDir, config.env, {
            builtin: config.builtin,
          });
          return Schema.decodeUnknownSync(SelfHostCapabilityCatalog)({
            object: 'capability_catalog',
            data: status.capabilities,
          });
        }),
      ),
  ).pipe(Layer.provide(runtimeLayers));

  const workflowCatalogHandlers = HttpApiBuilder.group(
    SelfHostCoreApi,
    'workflow-catalog',
    (handlers) =>
      handlers
        .handle('catalog', () =>
          Effect.gen(function* () {
            const config = yield* ServeRuntimeConfig;
            const status = createSelfHostRuntimeStatus(config.bundleDir, config.env, {
              builtin: config.builtin,
            });
            return Schema.decodeUnknownSync(SelfHostWorkflowCatalogSchema)(workflowCatalog(status));
          }),
        )
        .handle('deprecatedRecipes', () =>
          Effect.fail(
            HttpApiSchema.withHeaders({
              body: {
                object: 'error' as const,
                code: 'GONE' as const,
                error: 'gone',
                message: 'Use /v1/workflows/catalog (the cloud-canonical workflows resource).',
                successor: '/v1/workflows/catalog',
              },
              headers: {
                deprecation: 'true',
                link: '</v1/workflows/catalog>; rel="successor-version"',
              },
            }),
          ),
        ),
  ).pipe(Layer.provide(runtimeLayers));

  const documentHandlers = HttpApiBuilder.group(SelfHostCoreApi, 'documents', (handlers) =>
    handlers
      .handle('graph', ({ params }) =>
        effectDocumentGraph(params.id).pipe(
          Effect.map((graph) => Schema.decodeUnknownSync(SelfHostJsonObject)(graph)),
        ),
      )
      .handle('markdown', ({ params }) =>
        effectDocumentGraph(params.id).pipe(Effect.map(markdownFromGraph)),
      )
      .handle('status', ({ params }) =>
        effectDocumentGraph(params.id).pipe(
          Effect.map((graph) =>
            Schema.decodeUnknownSync(SelfHostJsonObject)(documentStatusFromGraph(graph)),
          ),
        ),
      )
      .handle('pages', ({ params }) =>
        effectDocumentGraph(params.id).pipe(
          Effect.map((graph) =>
            Schema.decodeUnknownSync(Schema.Array(SelfHostJsonObject))(pagesFromGraph(graph)),
          ),
        ),
      )
      .handle('nodes', ({ params }) =>
        effectDocumentGraph(params.id).pipe(
          Effect.map((graph) =>
            Schema.decodeUnknownSync(SelfHostJsonObject)(entitiesFromGraph(graph)),
          ),
        ),
      )
      .handle('reviews', ({ params }) =>
        effectDocumentGraph(params.id).pipe(
          Effect.map((graph) =>
            Schema.decodeUnknownSync(SelfHostJsonObject)({
              object: 'review_list',
              document_id: params.id,
              data: graph.reviews,
            }),
          ),
        ),
      )
      .handle('findings', ({ params }) =>
        effectDocumentGraph(params.id).pipe(
          Effect.map((graph) =>
            Schema.decodeUnknownSync(SelfHostJsonObject)({
              object: 'finding_list',
              document_id: params.id,
              data: graph.findings,
            }),
          ),
        ),
      )
      .handle('redactions', ({ params }) =>
        effectDocumentGraph(params.id).pipe(
          Effect.map((graph) =>
            Schema.decodeUnknownSync(SelfHostJsonObject)({
              object: 'redaction_list',
              document_id: params.id,
              data: graph.redactions,
            }),
          ),
        ),
      ),
  ).pipe(Layer.provide(runtimeLayers));

  const sourceHandlers = HttpApiBuilder.group(SelfHostCoreApi, 'sources', (handlers) =>
    handlers.handle('fromN8n', ({ payload }) => effectN8nSource(payload)),
  ).pipe(Layer.provide(authorization), Layer.provide(runtimeLayers));

  const legacyDocumentHandlers = HttpApiBuilder.group(
    SelfHostCoreApi,
    'legacy-documents',
    (handlers) =>
      handlers
        .handle('status', ({ params }) =>
          effectDocumentGraph(params.id).pipe(
            Effect.map((graph) =>
              Schema.decodeUnknownSync(SelfHostJsonObject)(documentStatusFromGraph(graph)),
            ),
          ),
        )
        .handle('markdown', ({ params }) =>
          effectDocumentGraph(params.id).pipe(Effect.map(markdownFromGraph)),
        )
        .handle('pages', ({ params }) =>
          effectDocumentGraph(params.id).pipe(
            Effect.map((graph) =>
              Schema.decodeUnknownSync(Schema.Array(SelfHostJsonObject))(pagesFromGraph(graph)),
            ),
          ),
        )
        .handle('page', ({ params }) =>
          effectDocumentGraph(params.id).pipe(
            Effect.flatMap((graph) => {
              const pageNumber = Number.parseInt(params.page, 10);
              const page = pagesFromGraph(graph).find((item) => item.page === pageNumber);
              return page
                ? Effect.succeed(Schema.decodeUnknownSync(SelfHostJsonObject)(page))
                : Effect.fail({ object: 'error' as const, error: 'page not found' });
            }),
          ),
        )
        .handle('nodes', ({ params }) =>
          effectDocumentGraph(params.id).pipe(
            Effect.map((graph) =>
              Schema.decodeUnknownSync(SelfHostJsonObject)(entitiesFromGraph(graph)),
            ),
          ),
        )
        .handle('download', ({ params }) => effectOriginalPdf(params.id))
        .handle('uploadUrl', ({ params, payload }) => effectUploadUrl(params.id, payload.url))
        .handle('upload', ({ headers, params, payload }) =>
          effectUpload(params.id, payload, headers),
        ),
  ).pipe(Layer.provide(authorization), Layer.provide(runtimeLayers));

  const workflowHandlers = HttpApiBuilder.group(SelfHostCoreApi, 'workflows', (handlers) =>
    handlers
      .handle('run', ({ payload }) => effectWorkflowPost(resolved, store, payload))
      .handle('getRun', ({ params }) => effectReadWorkflowRun(params.id))
      .handle('getCapabilityRun', ({ params }) => effectReadCapabilityRun(params.id)),
  ).pipe(Layer.provide(authorization), Layer.provide(runtimeLayers));

  const routes = HttpApiBuilder.layer(SelfHostCoreApi).pipe(
    Layer.provide([
      systemHandlers,
      runtimeHandlers,
      workflowCatalogHandlers,
      documentHandlers,
      sourceHandlers,
      legacyDocumentHandlers,
      workflowHandlers,
    ]),
    Layer.provide(HttpServer.layerServices),
  );
  const runtime = HttpRouter.toWebHandler(routes, { disableLogger: true });
  const handle = runtime.handler as (request: Request) => Promise<Response>;
  return {
    handler: async (request) => {
      await store.ready();
      return handle(request);
    },
    dispose: runtime.dispose,
  };
}

export function createSelfHostEffectHandler(
  options: SelfHostRuntimeRequestOptions,
): SelfHostEffectHandler {
  const resolved = resolveServeRuntimeOptions(options);
  const effectStore = createEffectServeStore(resolved);
  const store = toPromiseStore(effectStore);
  const runtime = createSelfHostEffectHandlerWithStore(resolved, effectStore, store);
  return {
    handler: runtime.handler,
    async dispose() {
      try {
        await runtime.dispose();
      } finally {
        await store.close();
      }
    },
  };
}

type SelfHostAppResources = {
  effectRuntime: SelfHostEffectHandler;
  store: ServeStore;
};

const selfHostResourcesByApp = new WeakMap<object, SelfHostAppResources>();

export async function disposeSelfHostApp(app: Hono<SelfHostHonoEnv>): Promise<void> {
  const resources = selfHostResourcesByApp.get(app);
  selfHostResourcesByApp.delete(app);
  if (!resources) return;
  try {
    await resources.effectRuntime.dispose();
  } finally {
    await resources.store.close();
  }
}

function clientProviderCatalog(): Record<string, unknown> {
  const provider = clientProviderSpecSchema.parse({
    id: 'local',
    displayName: 'Local machine',
    api: 'local',
    envKeys: [],
    models: [],
    keyHint: 'No key required',
    delivery: 'on_device',
  });
  return {
    object: 'client_provider_list',
    data: [{ ...provider, ready: true, status: 'Ready', isDefault: true }],
  };
}

function clientParserCatalog(): Record<string, unknown> {
  const parser = clientParserSpecSchema.parse({
    id: 'text-layer',
    displayName: 'Local text + OCR',
    version: '0.1.0',
    requires: 'none',
    defaults: {},
  });
  return {
    object: 'client_parser_list',
    data: [parser],
  };
}

function localSourcePath(root: string, source: string): string | null {
  try {
    const publicRoot = realpathSync(root);
    const candidate = realpathSync(resolve(root, source));
    const insideRoot = candidate === publicRoot || candidate.startsWith(`${publicRoot}${sep}`);
    if (!insideRoot || extname(candidate).toLowerCase() !== '.pdf') return null;
    if (!statSync(candidate).isFile()) return null;
    return candidate;
  } catch {
    return null;
  }
}

async function handleClientDocumentCreate(
  c: Context<SelfHostHonoEnv>,
  hub: ClientEventHub,
): Promise<Response> {
  const { root, dataDir, store } = c.get('rc');
  const contentType = c.req.header('content-type')?.split(';')[0].trim().toLowerCase();
  let bytes: Uint8Array;
  let filename: string;

  if (contentType === 'application/pdf' || contentType === 'application/octet-stream') {
    bytes = new Uint8Array(await c.req.raw.arrayBuffer());
    filename = decodedFilename(c.req.header('x-file-name') ?? '').trim() || 'document.pdf';
  } else {
    const body = await readRequestJson(c.req.raw);
    if (!body) {
      return clientJsonResponse(
        { object: 'error', error: 'request body must be a JSON object' },
        400,
      );
    }
    const source = typeof body.source === 'string' ? body.source : '';
    const path = localSourcePath(root, source);
    if (!path) {
      return clientJsonResponse(
        {
          object: 'error',
          error: 'source must be a PDF inside the directory where okra serve was started',
        },
        400,
      );
    }
    bytes = new Uint8Array(readFileSync(path));
    filename =
      typeof body.title === 'string' && body.title.trim()
        ? body.title.trim()
        : (path.split(sep).at(-1) ?? 'document.pdf');
  }

  if (!bytes.byteLength)
    return clientJsonResponse({ object: 'error', error: 'PDF body is required' }, 400);
  const header = Buffer.from(bytes.subarray(0, Math.min(bytes.byteLength, 1024))).toString(
    'latin1',
  );
  if (!header.includes('%PDF-')) {
    return clientJsonResponse(
      { object: 'error', error: 'document is not a recognizable PDF' },
      415,
    );
  }
  const hash = sha256Hex(bytes);
  const requestedId = c.req.header('x-document-id');
  const documentId = isSelfHostResourceId(requestedId) ? requestedId : stableId('doc', hash);
  const source = writeSource(dataDir, {
    object: 'source_ref',
    id: stableId('src_upload', { documentId, hash }),
    kind: 'local_upload',
    status: 'accepted',
    received_at: new Date().toISOString(),
    filename,
    content_type: 'application/pdf',
    size_bytes: bytes.byteLength,
    sha256: hash,
  });
  await store.writeOriginal(documentId, bytes);
  const graph = await store.writeGraph(
    createStarterGraph(source, documentId, {
      pageCount: pageCountFromPdf(bytes),
    }),
  );
  const document = clientDocumentFromGraph(graph);
  hub.publish(clientEvent({ type: 'document.opened', document }, Date.now()));
  return clientJsonResponse(document, 201);
}

function clientLayoutBlock(block: Record<string, unknown>): ClientLayoutBlock | null {
  const bbox = isRecord(block.bbox) ? block.bbox : null;
  const x = typeof bbox?.x === 'number' ? bbox.x : null;
  const y = typeof bbox?.y === 'number' ? bbox.y : null;
  const w = typeof bbox?.w === 'number' ? bbox.w : null;
  const h = typeof bbox?.h === 'number' ? bbox.h : null;
  const text = typeof block.text === 'string' ? block.text : '';
  const page = typeof block.page_number === 'number' ? block.page_number : 1;
  if (x === null || y === null || w === null || h === null || !text.trim()) return null;
  return clientLayoutBlockSchema.parse({
    label: typeof block.kind === 'string' ? block.kind : 'text',
    bbox: apiBboxToClient({ x, y, w, h }),
    text,
    page,
  });
}

function ensureGraphPages(graph: StarterDocumentGraph, pageCount: number): void {
  const existing = new Map(graph.pages.map((page) => [page.page_number as number, page]));
  graph.pages = Array.from({ length: Math.max(1, pageCount) }, (_, index) => {
    const pageNumber = index + 1;
    return (
      existing.get(pageNumber) ?? {
        page_number: pageNumber,
        width: 612,
        height: 792,
        rendered_assets: [],
        text_state: 'uploaded',
      }
    );
  });
}

async function failedClientRun(
  record: ClientRunRecord,
  message: string,
  dataDir: string,
  hub: ClientEventHub,
): Promise<ClientRunRecord> {
  const now = new Date().toISOString();
  record.run = clientRunSchema.parse({ ...record.run, status: 'failed', updatedAt: now });
  const event = clientEvent(
    { type: 'run.failed', runId: record.run.id, message },
    record.events.length,
  );
  record.events.push(event);
  record.error = message;
  writeClientRunRecord(dataDir, record);
  hub.publish(event);
  return record;
}

async function executeClientParse(
  graph: StarterDocumentGraph,
  requestBody: unknown,
  dataDir: string,
  store: ServeStore,
  hub: ClientEventHub,
  existing?: ClientRunRecord,
  parseDocument: typeof localParse = localParse,
): Promise<ClientRunRecord> {
  const request = clientParseRequestSchema.parse(requestBody);
  const parserId = request.parserId;
  const now = new Date().toISOString();
  const runId = existing?.run.id ?? `run_${randomUUID().replaceAll('-', '')}`;
  const pages: ClientPageLifecycle[] = graph.pages.map((page) => ({
    parserId,
    page: page.page_number as number,
    state: 'in_progress',
    updatedAt: now,
  }));
  const run = clientRunSchema.parse({
    object: 'client_run',
    id: runId,
    documentId: graph.document.id,
    parserId,
    ...(request.providerId ? { providerId: request.providerId } : {}),
    ...(request.model ? { model: request.model } : {}),
    status: 'running',
    pageCount: graph.pages.length,
    pages,
    createdAt: existing?.run.createdAt ?? now,
    updatedAt: now,
  });
  const record: ClientRunRecord = existing
    ? { ...existing, run }
    : { object: 'client_run_record', run, events: [] };
  delete record.error;
  const started = clientEvent({ type: 'run.started', run }, record.events.length);
  record.events.push(started);
  writeClientRunRecord(dataDir, record);
  hub.publish(started);

  if (parserId !== 'text-layer') {
    return failedClientRun(
      record,
      `Parser "${parserId}" is not installed in this okra serve runtime.`,
      dataDir,
      hub,
    );
  }
  if (request.providerId && request.providerId !== 'local') {
    return failedClientRun(
      record,
      `Provider "${request.providerId}" is not installed in this okra serve runtime.`,
      dataDir,
      hub,
    );
  }
  if (request.model) {
    return failedClientRun(record, 'Parser "text-layer" does not accept a model.', dataDir, hub);
  }

  const startedAt = Date.now();
  const parsed = await store.withMaterializedOriginal(
    graph.document.id,
    ({ dataDir: parseDataDir, documentId }) =>
      runNodeLocalParseProgram({
        dataDir: parseDataDir,
        documentId,
        runId,
        idPrefix: `blk_client_${runId}`,
        parseDocument,
      }),
  );
  if (!parsed.ok) {
    return failedClientRun(record, parsed.reason ?? 'Local parser unavailable.', dataDir, hub);
  }

  ensureGraphPages(graph, parsed.pageCount ?? graph.pages.length);
  graph.blocks = graph.blocks.filter((block) => !String(block.id ?? '').startsWith('blk_client_'));
  for (const block of parsed.blocks) graph.blocks.push(block);
  const parsedBlocks = parsed.blocks.flatMap((block) => {
    const value = clientLayoutBlock(block);
    return value ? [value] : [];
  });
  if (!parsedBlocks.length && request.allowEmpty !== true) {
    await store.writeGraph(graph);
    return failedClientRun(
      record,
      'The parser completed without extractable text. Retry with OCR or allowEmpty.',
      dataDir,
      hub,
    );
  }

  const pageParses: ClientPageParse[] = graph.pages.map((page) => {
    const pageNumber = page.page_number as number;
    const blocks = parsedBlocks.filter((block) => block.page === pageNumber);
    const markdown = blocks.map((block) => block.text).join('\n\n');
    page.text_state = blocks.length ? (parsed.ocrUsed ? 'ocr' : 'text') : 'empty';
    return {
      page: pageNumber,
      markdown,
      blocks,
      usage: { inputTokens: 0, outputTokens: 0, thinkingTokens: 0 },
    };
  });
  const markdown = [
    `# ${graph.document.filename ?? graph.document.id}`,
    ...pageParses.flatMap((page) => [`\n## Page ${page.page}\n`, page.markdown]),
  ]
    .join('\n')
    .trim();
  const result: ClientDocumentParse = clientDocumentParseSchema.parse({
    object: 'client_document_parse',
    markdown,
    pages: pageParses,
    blocks: parsedBlocks,
    usage: { inputTokens: 0, outputTokens: 0, thinkingTokens: 0 },
    meta: {
      parserId,
      pageCount: graph.pages.length,
      durationMs: Date.now() - startedAt,
      warnings: pageParses
        .filter((page) => !page.blocks.length)
        .map((page) => `Page ${page.page} has no extractable text.`),
    },
  });
  const artifacts: ClientArtifacts = {
    markdown: result.markdown,
    blocks: result.blocks,
    manifest: result.meta,
  };
  const completedAt = new Date().toISOString();
  const completedPages: ClientPageLifecycle[] = pageParses.map((page) => ({
    parserId,
    page: page.page,
    state: page.blocks.length ? 'done' : 'attention',
    ...(!page.blocks.length ? { detail: 'No extractable text on this page.' } : {}),
    updatedAt: completedAt,
  }));
  record.run = clientRunSchema.parse({
    ...run,
    status: 'succeeded',
    pageCount: graph.pages.length,
    pages: completedPages,
    updatedAt: completedAt,
  });
  record.artifacts = artifacts;
  delete record.error;
  for (const [index, page] of completedPages.entries()) {
    const event = clientEvent(
      {
        type: 'run.page',
        runId,
        page,
        parse: pageParses[index],
      },
      record.events.length,
    );
    record.events.push(event);
    hub.publish(event);
  }
  const completed = clientEvent(
    { type: 'run.completed', run: record.run, artifacts },
    record.events.length,
  );
  record.events.push(completed);
  await store.writeGraph(graph);
  writeClientRunRecord(dataDir, record);
  hub.publish(completed);
  return record;
}

export function createSelfHostApp(options: SelfHostRuntimeRequestOptions): Hono<SelfHostHonoEnv> {
  const resolved = resolveServeRuntimeOptions(options);
  const app = new Hono<SelfHostHonoEnv>();
  const eventHub = createClientEventHub();
  const effectStore = createEffectServeStore(resolved);
  const store = toPromiseStore(effectStore);
  const effectRuntime = createSelfHostEffectHandlerWithStore(resolved, effectStore, store);
  selfHostResourcesByApp.set(app, { effectRuntime, store });

  // Per-request runtime context: re-read the bundle each request so a hot
  // bundle swap (and the test harness's fresh data dirs) are reflected.
  app.use('*', async (c, next) => {
    await store.ready();
    c.set('rc', buildSelfHostContext(resolved, store));
    await next();
  });

  // Browser sharing uses HTTP Basic for the whole same-origin workbench. API
  // automation uses bearer / x-okra-api-key on document and run routes (reads
  // and writes). Either credential may authorize a protected route when both
  // are configured. Health probes stay open in every mode; with only an API
  // key configured, provider/parser catalogs and the HTML shell stay open too
  // so loopback clients and reverse-proxy probes still work.
  app.use('*', async (c, next) => {
    const { auth } = c.get('rc');
    const pathname = new URL(c.req.url).pathname;
    if (OPEN_PROBE_ROUTES.has(pathname)) return next();
    if (auth.serverPassword && !hasAnyRuntimeCredential(c.req.raw, auth)) {
      return unauthorizedResponse(auth);
    }
    if (protectedByApiKey(pathname) && auth.apiKey && !hasAnyRuntimeCredential(c.req.raw, auth)) {
      return unauthorizedResponse(auth);
    }
    return next();
  });

  // -- okra.client.v1 workbench profile -----------------------------------
  app.get('/global/health', (c) =>
    clientJsonResponse({
      object: 'client_health',
      protocol: CLIENT_PROTOCOL_VERSION,
      healthy: true,
      version: c.get('rc').env.npm_package_version ?? 'dev',
      host: 'cli_serve',
      capabilities: [
        'providers',
        'parsers',
        'documents',
        'parse',
        'runs.events',
        'runs.resume',
        'redact',
      ],
    }),
  );

  app.get('/global/event', (c) => {
    const { dataDir } = c.get('rc');
    const connected = clientEvent({ type: 'server.connected', host: 'cli_serve' }, 0);
    const history = listClientRunRecords(dataDir).flatMap((record) => record.events);
    return clientEventStream(
      c.req.raw,
      [connected, ...history],
      eventHub,
      () => true,
      c.req.query('once') === '1',
    );
  });

  app.get('/provider', () => clientJsonResponse(clientProviderCatalog()));
  app.get('/parser', () => clientJsonResponse(clientParserCatalog()));

  app.post('/document', async (c) => handleClientDocumentCreate(c, eventHub));
  app.get('/document', async (c) => {
    const data = (await c.get('rc').store.listGraphs())
      .map(clientDocumentFromGraph)
      .sort((left, right) => right.openedAt.localeCompare(left.openedAt));
    return clientJsonResponse({ object: 'client_document_list', data });
  });
  app.get('/document/:id', (c) =>
    withGraph(c, (graph) => clientJsonResponse(clientDocumentFromGraph(graph))),
  );
  app.post('/document/:id/parse', async (c) => {
    const { dataDir, store } = c.get('rc');
    const documentId = c.req.param('id');
    if (!isSelfHostResourceId(documentId)) {
      return clientJsonResponse({ object: 'error', error: 'invalid document id' }, 400);
    }
    const graph = await store.readGraph(documentId);
    if (!graph) return clientJsonResponse({ object: 'error', error: 'document not found' }, 404);
    const body = await readRequestJson(c.req.raw);
    if (!body) {
      return clientJsonResponse(
        { object: 'error', error: 'request body must be a JSON object' },
        400,
      );
    }
    try {
      const record = await executeClientParse(
        graph,
        { ...body, parserId: typeof body.parserId === 'string' ? body.parserId : 'text-layer' },
        dataDir,
        store,
        eventHub,
        undefined,
        c.get('rc').options.clientParse,
      );
      return clientJsonResponse(
        { ...record.run, ...(record.error ? { error: record.error } : {}) },
        202,
      );
    } catch (error) {
      return clientJsonResponse(
        {
          object: 'error',
          error: error instanceof Error ? error.message : String(error),
        },
        400,
      );
    }
  });

  app.get('/run', (c) => {
    const { dataDir } = c.get('rc');
    return clientJsonResponse({
      object: 'client_run_list',
      data: listClientRunRecords(dataDir).map((record) => ({
        ...record.run,
        ...(record.error ? { error: record.error } : {}),
      })),
    });
  });
  app.get('/run/:id', (c) => {
    const { dataDir } = c.get('rc');
    const record = readClientRunRecord(dataDir, c.req.param('id'));
    return record
      ? clientJsonResponse({ ...record.run, ...(record.error ? { error: record.error } : {}) })
      : clientJsonResponse({ object: 'error', error: 'run not found' }, 404);
  });
  app.get('/run/:id/events', (c) => {
    const { dataDir } = c.get('rc');
    const runId = c.req.param('id');
    const record = readClientRunRecord(dataDir, runId);
    if (!record) return clientJsonResponse({ object: 'error', error: 'run not found' }, 404);
    return clientEventStream(
      c.req.raw,
      record.events,
      eventHub,
      (event) => eventRunId(event) === runId,
      c.req.query('once') === '1',
    );
  });
  app.get('/run/:id/artifacts', (c) => {
    const { dataDir } = c.get('rc');
    const record = readClientRunRecord(dataDir, c.req.param('id'));
    if (!record) return clientJsonResponse({ object: 'error', error: 'run not found' }, 404);
    return record.artifacts
      ? clientJsonResponse(record.artifacts)
      : clientJsonResponse({ object: 'error', error: 'run artifacts are not ready' }, 409);
  });
  const analyzeRunPii = (dataDir: string, runId: string): Response => {
    const record = readClientRunRecord(dataDir, runId);
    if (!record) return clientJsonResponse({ object: 'error', error: 'run not found' }, 404);
    if (record.run.status !== 'succeeded' || !record.artifacts) {
      return clientJsonResponse(
        { object: 'error', error: 'run must succeed before PII analysis' },
        409,
      );
    }
    record.redaction = detectClientPii(record);
    writeClientRunRecord(dataDir, record);
    return clientJsonResponse(record.redaction);
  };
  const readRunPiiAnalysis = (dataDir: string, runId: string): Response => {
    const record = readClientRunRecord(dataDir, runId);
    if (!record) return clientJsonResponse({ object: 'error', error: 'run not found' }, 404);
    return record.redaction
      ? clientJsonResponse(record.redaction)
      : clientJsonResponse({ object: 'error', error: 'PII analysis not found' }, 404);
  };
  app.post('/run/:id/pii/analyze', (c) => {
    return analyzeRunPii(c.get('rc').dataDir, c.req.param('id'));
  });
  app.get('/run/:id/pii/analysis', (c) => {
    return readRunPiiAnalysis(c.get('rc').dataDir, c.req.param('id'));
  });
  // Compatibility aliases for desktop-v1.0.0-rc.17 clients. Do not advertise these.
  app.post('/run/:id/detect', (c) => {
    return analyzeRunPii(c.get('rc').dataDir, c.req.param('id'));
  });
  app.get('/run/:id/redactions', (c) => {
    return readRunPiiAnalysis(c.get('rc').dataDir, c.req.param('id'));
  });
  app.post('/run/:id/cancel', (c) => {
    const { dataDir } = c.get('rc');
    const record = readClientRunRecord(dataDir, c.req.param('id'));
    if (!record) return clientJsonResponse({ object: 'error', error: 'run not found' }, 404);
    if (
      record.run.status === 'succeeded' ||
      record.run.status === 'failed' ||
      record.run.status === 'cancelled'
    ) {
      return clientJsonResponse(
        { object: 'error', error: `run is already ${record.run.status}` },
        409,
      );
    }
    const now = new Date().toISOString();
    record.run = clientRunSchema.parse({ ...record.run, status: 'cancelled', updatedAt: now });
    const event = clientEvent(
      { type: 'run.cancelled', runId: record.run.id },
      record.events.length,
    );
    record.events.push(event);
    writeClientRunRecord(dataDir, record);
    eventHub.publish(event);
    return clientJsonResponse(record.run);
  });
  app.post('/run/:id/resume', async (c) => {
    const { dataDir, store } = c.get('rc');
    const record = readClientRunRecord(dataDir, c.req.param('id'));
    if (!record) return clientJsonResponse({ object: 'error', error: 'run not found' }, 404);
    if (record.run.status !== 'failed' && record.run.status !== 'cancelled') {
      return clientJsonResponse(
        { object: 'error', error: 'only failed or cancelled runs can resume' },
        409,
      );
    }
    const graph = await store.readGraph(record.run.documentId);
    if (!graph) return clientJsonResponse({ object: 'error', error: 'document not found' }, 404);
    const next = await executeClientParse(
      graph,
      {
        parserId: record.run.parserId,
        ...(record.run.providerId ? { providerId: record.run.providerId } : {}),
        ...(record.run.model ? { model: record.run.model } : {}),
      },
      dataDir,
      store,
      eventHub,
      record,
      c.get('rc').options.clientParse,
    );
    return clientJsonResponse({ ...next.run, ...(next.error ? { error: next.error } : {}) }, 202);
  });

  // The Effect HttpApi/OpenAPI document is the only route inventory. Hono is a
  // Node/static transport adapter and registers every API leaf from it.
  const transportRoutes = Effect.runSync(makeSelfHostApiRegistry().transportRoutes());
  for (const { method, path } of transportRoutes) {
    app.on(method, path, (c) => effectRuntime.handler(c.req.raw));
  }

  // -- static assets + SPA fallback (GET/HEAD only) -------------------------
  app.get('*', (c) => {
    const { root, status, options: opts } = c.get('rc');
    const pathname = new URL(c.req.url).pathname;
    // The client-protocol workbench is the default UI for both zero-config and
    // validated bundles. An explicit --public-dir remains the escape hatch for
    // a custom shell without making an old bundle's generated index win.
    if (pathname === '/' && !opts.publicDir) {
      return textResponse(fallbackIndex(status), 200, 'text/html; charset=utf-8');
    }
    const publicDir = resolve(opts.publicDir ?? join(root, 'public'));
    const staticPath = resolveStaticPath(publicDir, pathname);
    if (staticPath) {
      return staticFileResponse(staticPath);
    }
    if (pathname === '/') {
      return textResponse(fallbackIndex(status), 200, 'text/html; charset=utf-8');
    }
    return jsonResponse({ object: 'error', error: 'not found' }, 404);
  });

  // Unmatched non-GET methods preserve the legacy 405 contract.
  app.notFound(() => jsonResponse({ object: 'error', error: 'method not allowed' }, 405));

  app.onError((error) =>
    jsonResponse(
      { object: 'error', error: error instanceof Error ? error.message : String(error) },
      500,
    ),
  );

  return app;
}

export const handleSelfHostRuntimeRequestEffect = Effect.fn('handleSelfHostRuntimeRequestEffect')(
  function* (
    request: Request,
    options: SelfHostRuntimeRequestOptions,
  ): Effect.fn.Return<Response, SelfHostRuntimeRequestError> {
    const app = yield* Effect.try({
      try: () => createSelfHostApp(options),
      catch: (cause) => makeSelfHostRuntimeRequestError('create-app', cause),
    });
    return yield* Effect.tryPromise({
      try: async () => {
        try {
          return await app.fetch(request);
        } finally {
          await disposeSelfHostApp(app);
        }
      },
      catch: (cause) => makeSelfHostRuntimeRequestError('request', cause),
    });
  },
);

export async function handleSelfHostRuntimeRequest(
  request: Request,
  options: SelfHostRuntimeRequestOptions,
): Promise<Response> {
  const result = await Effect.runPromise(
    Effect.result(handleSelfHostRuntimeRequestEffect(request, options)),
  );
  if (Result.isSuccess(result)) return result.success;
  throw selfHostRuntimeRequestErrorCause(result.failure);
}

export async function startSelfHostRuntimeServer(
  options: SelfHostRuntimeServerOptions,
): Promise<StartedSelfHostRuntimeServer> {
  const host = options.host ?? '127.0.0.1';
  const port = options.port ?? 8787;
  const app = createSelfHostApp(options);
  const resources = selfHostResourcesByApp.get(app)!;

  try {
    await resources.store.ready();
  } catch (error) {
    await disposeSelfHostApp(app);
    throw error;
  }

  return new Promise((resolveServer, reject) => {
    let settled = false;
    try {
      const server = serve({ fetch: app.fetch, hostname: host, port }, (info) => {
        settled = true;
        const displayHost = host === '0.0.0.0' ? '127.0.0.1' : host;
        resolveServer({
          server: server as unknown as Server,
          url: `http://${displayHost}:${info.port}`,
          close: () =>
            new Promise<void>((resolveClose, rejectClose) => {
              (server as unknown as Server).close((error?: Error) => {
                disposeSelfHostApp(app).then(
                  () => (error ? rejectClose(error) : resolveClose()),
                  rejectClose,
                );
              });
            }),
        });
      });
      (server as unknown as Server).once('error', (error: Error) => {
        if (!settled) {
          disposeSelfHostApp(app).then(
            () => reject(error),
            () => reject(error),
          );
        }
      });
    } catch (error) {
      disposeSelfHostApp(app).then(
        () => reject(error),
        () => reject(error),
      );
    }
  });
}
